/**
 * Elite Batch 19 Part 5 — Workflow Templates 1828–1847
 * Marketplace listing submission prep, marketplace partner outreach, meeting booked
 * suppression sync, meeting completed contact expansion, missing company domain
 * resolution, missing LinkedIn resolution, multi-threading suggestions,
 * multi-threading within account, NPS promoter warm intro pipeline, named account
 * protection gate, negative sentiment displacement pitch, new-hire trigger outreach,
 * newsletter click intent handoff, newsletter sponsorship pitch, niche forum question
 * trigger, no-response follow-up sequence, no-show reschedule automation, objection
 * extraction response, objection library match response, out-of-office rescheduler.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 19 PART 5 — TEMPLATES 1828–1847
// =============================================================================

export const ELITE_BATCH19_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1828. MARKETPLACE LISTING SUBMISSION PREP (7 nodes)
  // ===================================================================

  "Marketplace Listing Submission Prep": {
    summary:
      "Runs weekly on a schedule, AI-researches marketplace listing requirements, audits the current listing against requirements with runCode, AI-analyzes competitor listings, AI-generates optimized content, builds a screenshot asset checklist with runCode, and delivers the submission package via email and Slack.",
    nodes: [
      trigger("Schedule: Weekly marketplace listing prep", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to prepare and optimize marketplace listing submissions by researching requirements, auditing current listings, analyzing competitors, and generating optimized content packages",
        setup:
          "Configure OpenAI API key for AI research and content generation. Set marketplace API credentials for listing data retrieval. Configure Slack webhook for review delivery. Set email credentials for submission package delivery.",
        inputFields: [],
      }),

      // 2. AI requirements research
      aiNode(
        "2",
        "AI Research Marketplace Requirements",
        280,
        "You are a marketplace listing optimization specialist. Research and compile the latest listing requirements for the target marketplace including character limits, required fields, image dimensions, video requirements, category taxonomy, keyword limits, and compliance rules. Return JSON: {title_char_limit, description_char_limit, required_fields, image_specs, video_specs, category_options, keyword_limit, compliance_rules, recent_policy_changes, submission_checklist}",
        "Research the current listing requirements and submission guidelines for the target marketplace. Include all mandatory fields, content specifications, and recent policy updates.",
        [
          { field: "title_char_limit", as: "title_char_limit", type: "string" },
          {
            field: "description_char_limit",
            as: "description_char_limit",
            type: "string",
          },
          { field: "required_fields", as: "required_fields", type: "string" },
          { field: "image_specs", as: "image_specs", type: "string" },
          { field: "video_specs", as: "video_specs", type: "string" },
          { field: "category_options", as: "category_options", type: "string" },
          { field: "keyword_limit", as: "keyword_limit", type: "string" },
          { field: "compliance_rules", as: "compliance_rules", type: "string" },
          {
            field: "submission_checklist",
            as: "submission_checklist",
            type: "string",
          },
        ],
      ),

      // 3. Current listing audit runCode
      n("3", "runCode", "Audit Current Listing Against Requirements", 520, {
        runCodeDetails: {
          code: `const requiredFields = (variables.required_fields || '').split(',').map(f => f.trim()).filter(Boolean);
const titleLimit = parseInt(variables.title_char_limit) || 80;
const descLimit = parseInt(variables.description_char_limit) || 4000;
let gaps = [];
let compliance_issues = [];
let completeness_score = 0;
const totalChecks = requiredFields.length + 3;
let passedChecks = 0;
requiredFields.forEach(field => {
  if (field.toLowerCase().includes('video') || field.toLowerCase().includes('demo')) {
    gaps.push('Missing: ' + field + ' — needs creation');
  } else {
    passedChecks++;
  }
});
if (titleLimit < 100) {
  compliance_issues.push('Title must be under ' + titleLimit + ' chars — verify current title length');
} else {
  passedChecks++;
}
if (descLimit < 2000) {
  compliance_issues.push('Description limit tight at ' + descLimit + ' chars — needs condensing');
} else {
  passedChecks++;
}
passedChecks++;
completeness_score = Math.round((passedChecks / totalChecks) * 100);
output.listing_gaps = gaps.length > 0 ? gaps.join('; ') : 'No gaps detected';
output.compliance_issues = compliance_issues.length > 0 ? compliance_issues.join('; ') : 'All compliance checks passed';
output.completeness_score = completeness_score;
output.total_gaps = gaps.length;
output.audit_status = completeness_score >= 80 ? 'ready_for_optimization' : 'needs_remediation';`,
          outputVariables: [
            { name: "listing_gaps", type: "string" },
            { name: "compliance_issues", type: "string" },
            { name: "completeness_score", type: "number" },
            { name: "total_gaps", type: "number" },
            { name: "audit_status", type: "string" },
          ],
        },
      }),

      // 4. AI competitor analysis
      aiNode(
        "4",
        "AI Analyze Competitor Listings",
        760,
        "You are a competitive marketplace analyst. Analyze how top-performing competitor listings are structured, what keywords they target, their unique selling propositions, their visual strategies, and their review/rating approaches. Identify differentiation opportunities. Return JSON: {top_competitor_keywords, competitor_usp_patterns, visual_strategy_insights, review_strategy, differentiation_opportunities, recommended_positioning, pricing_positioning, category_placement_advice}",
        "Analyze competitor listings in the marketplace. Our current gaps: {listing_gaps}. Compliance status: {compliance_issues}. Completeness: {completeness_score}%. Category options: {category_options}. Find opportunities to outperform competitors.",
        [
          {
            field: "top_competitor_keywords",
            as: "competitor_keywords",
            type: "string",
          },
          {
            field: "competitor_usp_patterns",
            as: "competitor_usp_patterns",
            type: "string",
          },
          {
            field: "differentiation_opportunities",
            as: "differentiation_opps",
            type: "string",
          },
          {
            field: "recommended_positioning",
            as: "recommended_positioning",
            type: "string",
          },
          {
            field: "pricing_positioning",
            as: "pricing_positioning",
            type: "string",
          },
          {
            field: "category_placement_advice",
            as: "category_advice",
            type: "string",
          },
          {
            field: "visual_strategy_insights",
            as: "visual_strategy",
            type: "string",
          },
        ],
      ),

      // 5. AI optimized content generation
      aiNode(
        "5",
        "AI Generate Optimized Listing Content",
        1000,
        "You are an expert marketplace copywriter. Generate an optimized listing including a compelling title within the character limit, a keyword-rich description, feature bullet points, integration highlights, and SEO-optimized metadata. Use competitor insights for differentiation. Return JSON: {optimized_title, optimized_description, feature_bullets, integration_highlights, seo_keywords, meta_description, call_to_action, a_b_test_variants}",
        "Title limit: {title_char_limit} chars. Description limit: {description_char_limit} chars. Keyword limit: {keyword_limit}. Competitor keywords: {competitor_keywords}. Differentiation: {differentiation_opps}. Positioning: {recommended_positioning}. Category: {category_advice}. Generate optimized listing content that outperforms competitors.",
        [
          { field: "optimized_title", as: "optimized_title", type: "string" },
          {
            field: "optimized_description",
            as: "optimized_description",
            type: "string",
          },
          { field: "feature_bullets", as: "feature_bullets", type: "string" },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
          { field: "call_to_action", as: "listing_cta", type: "string" },
          {
            field: "a_b_test_variants",
            as: "ab_test_variants",
            type: "string",
          },
        ],
      ),

      // 6. Screenshot asset checklist runCode
      n("6", "runCode", "Build Screenshot Asset Checklist", 1240, {
        runCodeDetails: {
          code: `const imageSpecs = (variables.image_specs || '').toLowerCase();
const videoSpecs = (variables.video_specs || '').toLowerCase();
const visualStrategy = (variables.visual_strategy || '').toLowerCase();
let assets = [];
assets.push('1. Hero screenshot (main product dashboard) — ' + (imageSpecs.includes('1280') ? '1280x800px' : '1920x1080px'));
assets.push('2. Feature highlight screenshot (key differentiator) — show ' + (variables.differentiation_opps || 'unique features'));
assets.push('3. Integration ecosystem screenshot — display connected tools');
assets.push('4. Analytics/ROI dashboard screenshot — demonstrate value');
assets.push('5. Onboarding flow screenshot — show ease of setup');
if (imageSpecs.includes('logo')) {
  assets.push('6. Company logo (square format, transparent background)');
}
if (videoSpecs && videoSpecs !== 'none') {
  assets.push('7. Product demo video — ' + (videoSpecs.includes('60') ? 'max 60 seconds' : 'max 90 seconds'));
}
let missingAssets = assets.filter(a => a.includes('demo video') || a.includes('Analytics'));
output.asset_checklist = assets.join('\\n');
output.total_assets_needed = assets.length;
output.missing_assets = missingAssets.length > 0 ? missingAssets.join('; ') : 'All assets available';
output.submission_ready = missingAssets.length === 0 ? 'yes' : 'pending_assets';`,
          outputVariables: [
            { name: "asset_checklist", type: "string" },
            { name: "total_assets_needed", type: "number" },
            { name: "missing_assets", type: "string" },
            { name: "submission_ready", type: "string" },
          ],
        },
      }),

      // 7. Submission package delivery email + Slack
      slackNode(
        "7",
        "Deliver Submission Package via Slack",
        1480,
        "Marketplace Listing Submission Package Ready\\n\\n*Optimized Title:* {optimized_title}\\n*Completeness:* {completeness_score}% | *Gaps:* {total_gaps}\\n*Submission Ready:* {submission_ready}\\n\\n*Feature Bullets:* {feature_bullets}\\n*SEO Keywords:* {seo_keywords}\\n*CTA:* {listing_cta}\\n\\n*Assets Needed ({total_assets_needed}):*\\n{asset_checklist}\\n\\n*Missing Assets:* {missing_assets}\\n*A/B Variants:* {ab_test_variants}\\n\\nReview and finalize for submission.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1829. MARKETPLACE PARTNER OUTREACH (7 nodes)
  // ===================================================================

  "Marketplace Partner Outreach": {
    summary:
      "Runs weekly on a schedule, AI-discovers complementary products in the marketplace, AI-analyzes partner product fit, AI-generates integration value propositions, discovers partner contacts via API, validates emails, and AI-drafts partnership proposals with HubSpot pipeline tracking.",
    nodes: [
      trigger("Schedule: Weekly marketplace partner discovery", "schedule", {
        schedule: "Every Wednesday at 10:00 AM",
        howItStarts:
          "Runs weekly to discover complementary marketplace products, analyze integration potential, generate value propositions, and initiate partnership outreach with validated contacts",
        setup:
          "Configure OpenAI API key for product analysis and proposal generation. Set marketplace API credentials for product discovery. Configure email validation service. Set HubSpot token for partnership pipeline tracking.",
        inputFields: [],
      }),

      // 2. AI complementary product discovery
      aiNode(
        "2",
        "AI Discover Complementary Products",
        280,
        "You are a marketplace partnership strategist. Identify products in adjacent categories that complement our solution. Focus on products with high ratings, active user bases, and no existing integration. Evaluate API availability and integration complexity. Return JSON: {partner_product_name, partner_company, partner_category, user_base_estimate, rating, api_available, integration_complexity, synergy_score, complementary_use_cases, partner_website}",
        "Discover complementary products in the marketplace that would create mutual value through integration. Focus on products that solve adjacent problems for our shared target audience.",
        [
          {
            field: "partner_product_name",
            as: "partner_product_name",
            type: "string",
          },
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "partner_category", as: "partner_category", type: "string" },
          {
            field: "user_base_estimate",
            as: "partner_user_base",
            type: "string",
          },
          {
            field: "api_available",
            as: "partner_api_available",
            type: "string",
          },
          {
            field: "integration_complexity",
            as: "integration_complexity",
            type: "string",
          },
          { field: "synergy_score", as: "synergy_score", type: "string" },
          {
            field: "complementary_use_cases",
            as: "complementary_use_cases",
            type: "string",
          },
          { field: "partner_website", as: "partner_website", type: "string" },
        ],
      ),

      // 3. AI partner product analysis
      aiNode(
        "3",
        "AI Analyze Partner Product Fit",
        520,
        "You are a technology partnership analyst. Deep-dive into the partner product to understand their positioning, customer profile overlap, technical architecture compatibility, co-marketing potential, and revenue sharing possibilities. Return JSON: {customer_overlap_score, technical_compatibility, co_marketing_potential, revenue_opportunity, risk_assessment, strategic_value, recommended_integration_type, partnership_tier}",
        "Partner: {partner_product_name} by {partner_company} | Category: {partner_category} | Users: {partner_user_base} | API: {partner_api_available} | Complexity: {integration_complexity} | Synergy: {synergy_score} | Use cases: {complementary_use_cases}",
        [
          {
            field: "customer_overlap_score",
            as: "customer_overlap_score",
            type: "string",
          },
          {
            field: "technical_compatibility",
            as: "technical_compatibility",
            type: "string",
          },
          {
            field: "co_marketing_potential",
            as: "co_marketing_potential",
            type: "string",
          },
          {
            field: "revenue_opportunity",
            as: "revenue_opportunity",
            type: "string",
          },
          { field: "strategic_value", as: "strategic_value", type: "string" },
          {
            field: "recommended_integration_type",
            as: "integration_type",
            type: "string",
          },
          { field: "partnership_tier", as: "partnership_tier", type: "string" },
        ],
      ),

      // 4. AI integration value proposition
      aiNode(
        "4",
        "AI Generate Integration Value Proposition",
        760,
        "You are an expert partnership proposal writer. Create a compelling integration value proposition including mutual benefits, customer impact, technical approach, co-marketing plan, and revenue projections. Return JSON: {value_prop_headline, mutual_benefits, customer_impact_statement, technical_approach_summary, co_marketing_plan, revenue_projection, proposal_subject, proposal_body}",
        "Partner: {partner_product_name} by {partner_company} | Overlap: {customer_overlap_score} | Compatibility: {technical_compatibility} | Co-marketing: {co_marketing_potential} | Revenue: {revenue_opportunity} | Integration type: {integration_type} | Tier: {partnership_tier} | Use cases: {complementary_use_cases}",
        [
          {
            field: "value_prop_headline",
            as: "value_prop_headline",
            type: "string",
          },
          { field: "mutual_benefits", as: "mutual_benefits", type: "string" },
          {
            field: "technical_approach_summary",
            as: "technical_approach",
            type: "string",
          },
          { field: "proposal_subject", as: "proposal_subject", type: "string" },
          { field: "proposal_body", as: "proposal_body", type: "string" },
        ],
      ),

      // 5. Partner contact discovery API
      n("5", "hitApi", "Discover Partner Contact via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{partner_company}" },
            {
              key: "person_titles",
              value:
                '["VP Partnerships","Head of Business Development","Director of Partnerships","VP Alliances"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "partner_contact_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "partner_contact_first",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "partner_contact_last",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "partner_contact_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "partner_contact_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Partner Contact Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={partner_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_validation_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI proposal draft + HubSpot pipeline creation
      hubspotPostNode(
        "7",
        "Create Partnership Deal in HubSpot",
        1480,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Partnership: {partner_company} — {partner_product_name}","pipeline":"partnerships","dealstage":"proposal_sent","description":"{value_prop_headline}. Benefits: {mutual_benefits}. Technical: {technical_approach}. Contact: {partner_contact_first} {partner_contact_last} ({partner_contact_title}) — {partner_contact_email}. Tier: {partnership_tier}.","amount":"0","partner_contact_email":"{partner_contact_email}"}',
          },
        ],
        [
          { name: "id", customName: "partnership_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1830. MEETING BOOKED SUPPRESSION SYNC (6 nodes)
  // ===================================================================

  "Meeting Booked Suppression Sync": {
    summary:
      "Fires when a meeting is booked via webhook, AI-extracts contact data, resolves the contact and account in HubSpot, detects active outbound sequences with runCode, executes suppression via API, updates the CRM status in HubSpot, and alerts the assigned rep via Slack.",
    nodes: [
      trigger("Webhook: Meeting booked event", "webhook_app", {
        source: "calendly",
        event: "meeting.booked",
        howItStarts:
          "Fires when a prospect books a meeting via Calendly or scheduling tool, sending the raw booking event with attendee details, meeting type, and calendar metadata",
        setup:
          "Configure Calendly webhook for meeting.booked events. Set HubSpot token for CRM lookups and status updates. Configure outbound sequence API credentials for suppression. Set Slack webhook for rep notifications.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI Extract Meeting Booking Data",
        280,
        "You are a meeting event parser. Extract the attendee email, attendee name, company name, meeting type, meeting date, meeting time, assigned rep email, rep name, meeting link, and any notes from the raw booking webhook. Return JSON: {attendee_email, attendee_name, attendee_company, meeting_type, meeting_date, meeting_time, rep_email, rep_name, meeting_link, booking_notes}",
        "Raw meeting booking webhook: {webhook_payload}. Extract all booking details regardless of field naming conventions or nested structures.",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_company", as: "attendee_company", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "meeting_link", as: "meeting_link", type: "string" },
        ],
      ),

      // 3. Contact account resolution HubSpot
      hubspotGetNode(
        "3",
        "Resolve Contact and Account in HubSpot",
        520,
        "crm/v3/objects/contacts/search?query={attendee_email}",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.company",
            customName: "crm_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_sequences_is_enrolled",
            customName: "sequence_enrolled",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "contact_owner_id",
            type: "string",
          },
        ],
      ),

      // 4. Active sequence detection runCode
      n("4", "runCode", "Detect Active Outbound Sequences", 760, {
        runCodeDetails: {
          code: `const isEnrolled = (variables.sequence_enrolled || '').toLowerCase();
const lifecycleStage = (variables.lifecycle_stage || '').toLowerCase();
let activeSequences = [];
let suppressionNeeded = false;
if (isEnrolled === 'true' || isEnrolled === 'yes') {
  activeSequences.push('HubSpot sequence enrollment detected');
  suppressionNeeded = true;
}
if (lifecycleStage === 'lead' || lifecycleStage === 'subscriber') {
  activeSequences.push('Contact in ' + lifecycleStage + ' stage — likely receiving nurture sequences');
  suppressionNeeded = true;
}
if (lifecycleStage === 'marketingqualifiedlead') {
  activeSequences.push('MQL stage — may be in marketing automation flows');
  suppressionNeeded = true;
}
output.active_sequences = activeSequences.length > 0 ? activeSequences.join('; ') : 'No active sequences detected';
output.suppression_needed = suppressionNeeded ? 'yes' : 'no';
output.sequence_count = activeSequences.length;
output.new_lifecycle_stage = 'salesqualifiedlead';`,
          outputVariables: [
            { name: "active_sequences", type: "string" },
            { name: "suppression_needed", type: "string" },
            { name: "sequence_count", type: "number" },
            { name: "new_lifecycle_stage", type: "string" },
          ],
        },
      }),

      // 5. CRM status update HubSpot
      hubspotPostNode(
        "5",
        "Update CRM Status and Suppress Sequences",
        1000,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"lifecyclestage":"{new_lifecycle_stage}","hs_sequences_is_enrolled":"false","notes_last_updated":"Meeting booked: {meeting_type} on {meeting_date} with {rep_name}. Suppressed {sequence_count} active sequences: {active_sequences}. Meeting link: {meeting_link}."}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "updated_stage",
            type: "string",
          },
        ],
      ),

      // 6. Confirmation + rep alert Slack
      slackNode(
        "6",
        "Alert Rep of Meeting Booking and Suppression",
        1240,
        "Meeting Booked — Sequences Suppressed\\n\\n*Attendee:* {attendee_name} ({attendee_email})\\n*Company:* {attendee_company}\\n*Meeting:* {meeting_type} on {meeting_date}\\n*Rep:* {rep_name}\\n*Meeting Link:* {meeting_link}\\n\\n*Suppression Details:*\\nSequences suppressed: {sequence_count}\\nDetails: {active_sequences}\\nLifecycle updated: {updated_stage}\\n\\nAll automated outreach paused for this contact.",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1831. MEETING COMPLETED CONTACT EXPANSION (8 nodes)
  // ===================================================================

  "Meeting Completed Contact Expansion": {
    summary:
      "Fires when a meeting completes via webhook, AI-analyzes meeting notes, identifies additional stakeholders with runCode, discovers new contacts via API, validates emails, AI-generates role-appropriate outreach, expands CRM contacts in HubSpot, and briefs the AE via Slack.",
    nodes: [
      trigger("Webhook: Meeting completed event", "webhook_app", {
        source: "gong",
        event: "call.completed",
        howItStarts:
          "Fires when a sales meeting completes in Gong or meeting tool, sending the raw call completion event with transcript data, attendees, and meeting metadata",
        setup:
          "Configure Gong webhook for call.completed events. Set OpenAI API key for notes analysis and outreach generation. Configure Apollo API for contact discovery. Set email validation API. Configure HubSpot token for CRM expansion. Set Slack webhook for AE briefings.",
        inputFields: [],
      }),

      // 2. AI notes analysis
      aiNode(
        "2",
        "AI Analyze Meeting Notes",
        280,
        "You are an expert sales call analyst. Analyze the raw meeting event to extract attendee names, titles, company, discussion topics, mentioned stakeholders who were not present, decision makers referenced, budget holder mentions, technical evaluator mentions, and next steps. Return JSON: {primary_contact_name, primary_contact_email, company_name, deal_id, discussed_topics, mentioned_stakeholders, decision_maker_names, budget_holder_names, technical_evaluator_names, next_steps, meeting_sentiment, expansion_urgency}",
        "Raw meeting completed webhook: {webhook_payload}. Extract all attendee and stakeholder information regardless of how fields are structured.",
        [
          {
            field: "primary_contact_name",
            as: "primary_contact_name",
            type: "string",
          },
          {
            field: "primary_contact_email",
            as: "primary_contact_email",
            type: "string",
          },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          {
            field: "mentioned_stakeholders",
            as: "mentioned_stakeholders",
            type: "string",
          },
          {
            field: "decision_maker_names",
            as: "decision_maker_names",
            type: "string",
          },
          {
            field: "budget_holder_names",
            as: "budget_holder_names",
            type: "string",
          },
          {
            field: "technical_evaluator_names",
            as: "technical_evaluator_names",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "expansion_urgency",
            as: "expansion_urgency",
            type: "string",
          },
        ],
      ),

      // 3. Stakeholder identification runCode
      n("3", "runCode", "Identify Missing Stakeholder Roles", 520, {
        runCodeDetails: {
          code: `const decisionMakers = (variables.decision_maker_names || '').split(',').map(n => n.trim()).filter(Boolean);
const budgetHolders = (variables.budget_holder_names || '').split(',').map(n => n.trim()).filter(Boolean);
const techEvaluators = (variables.technical_evaluator_names || '').split(',').map(n => n.trim()).filter(Boolean);
const mentioned = (variables.mentioned_stakeholders || '').split(',').map(n => n.trim()).filter(Boolean);
let targetRoles = [];
if (decisionMakers.length === 0) targetRoles.push('VP,Director,C-Suite');
if (budgetHolders.length === 0) targetRoles.push('CFO,VP Finance,Head of Procurement');
if (techEvaluators.length === 0) targetRoles.push('CTO,VP Engineering,Director of IT');
let allNames = [...new Set([...decisionMakers, ...budgetHolders, ...techEvaluators, ...mentioned])];
output.target_roles = targetRoles.length > 0 ? targetRoles.join(',') : 'Champion,Internal Advocate';
output.known_stakeholders = allNames.join(', ') || 'Primary contact only';
output.stakeholder_gap_count = targetRoles.length;
output.expansion_priority = targetRoles.length >= 2 ? 'high' : targetRoles.length === 1 ? 'medium' : 'low';`,
          outputVariables: [
            { name: "target_roles", type: "string" },
            { name: "known_stakeholders", type: "string" },
            { name: "stakeholder_gap_count", type: "number" },
            { name: "expansion_priority", type: "string" },
          ],
        },
      }),

      // 4. Contact discovery API
      n("4", "hitApi", "Discover Stakeholder Contacts via Apollo", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{company_name}" },
            { key: "person_titles", value: "{target_roles}" },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "new_contact_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "new_contact_first",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "new_contact_last",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "new_contact_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "new_contact_linkedin",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. Email validation API
      n("5", "hitApi", "Validate Discovered Contact Email", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={new_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_valid_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI role-appropriate outreach
      aiNode(
        "6",
        "AI Generate Role-Appropriate Outreach",
        1240,
        "You are an expert B2B sales outreach writer. Generate a warm, context-rich email that references the meeting with their colleague, explains why their involvement matters based on their specific role, and creates a natural reason to connect. Avoid sounding cold or generic. Return JSON: {outreach_subject, outreach_body, personalization_angle, value_prop_for_role, suggested_meeting_type}",
        "New contact: {new_contact_first} {new_contact_last}, {new_contact_title} at {company_name}. Their colleague {primary_contact_name} just completed a meeting with us. Topics discussed: {mentioned_stakeholders}. Next steps: {next_steps}. Role gaps identified: {target_roles}. Priority: {expansion_priority}.",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          {
            field: "personalization_angle",
            as: "personalization_angle",
            type: "string",
          },
          {
            field: "value_prop_for_role",
            as: "value_prop_for_role",
            type: "string",
          },
          {
            field: "suggested_meeting_type",
            as: "suggested_meeting_type",
            type: "string",
          },
        ],
      ),

      // 7. CRM contact expansion HubSpot
      hubspotPostNode(
        "7",
        "Create Expanded Contact in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{new_contact_email}","firstname":"{new_contact_first}","lastname":"{new_contact_last}","jobtitle":"{new_contact_title}","company":"{company_name}","hs_lead_status":"OPEN","notes_last_updated":"Discovered via meeting expansion — colleague {primary_contact_name} met with us. Role: {new_contact_title}. Personalization: {personalization_angle}. Value prop: {value_prop_for_role}. LinkedIn: {new_contact_linkedin}."}',
          },
        ],
        [
          { name: "id", customName: "new_hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),

      // 8. AE notification + strategy Slack
      slackNode(
        "8",
        "Brief AE on Contact Expansion Strategy",
        1720,
        "Meeting Expansion — New Stakeholder Identified\\n\\n*Original Meeting:* {primary_contact_name} at {company_name}\\n*Expansion Priority:* {expansion_priority}\\n*Stakeholder Gaps:* {stakeholder_gap_count}\\n\\n*New Contact:* {new_contact_first} {new_contact_last} — {new_contact_title}\\n*Email:* {new_contact_email} (Status: {email_valid_status})\\n*LinkedIn:* {new_contact_linkedin}\\n\\n*Outreach Strategy:*\\nAngle: {personalization_angle}\\nValue Prop: {value_prop_for_role}\\nSuggested Meeting: {suggested_meeting_type}\\n\\n*Known Stakeholders:* {known_stakeholders}\\n*Next Steps from Meeting:* {next_steps}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1832. MISSING COMPANY DOMAIN RESOLUTION (6 nodes)
  // ===================================================================

  "Missing Company Domain Resolution": {
    summary:
      "Runs daily on a schedule, queries HubSpot for contacts missing company domains, searches Clearbit by company name, runs a Google fallback domain search with runCode, verifies discovered domains with runCode, and updates the CRM with resolved domains plus logs results to Sheets.",
    nodes: [
      trigger("Schedule: Daily missing domain resolution", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs daily to find CRM contacts with missing company domains, resolve domains via Clearbit and Google fallback, verify discovered domains, and update CRM records in bulk",
        setup:
          "Configure HubSpot token for CRM queries and updates. Set Clearbit API key for domain lookups. Configure Google Custom Search API for fallback resolution. Set Google Sheets credentials for resolution logging.",
        inputFields: [],
      }),

      // 2. Missing domain query HubSpot
      hubspotGetNode(
        "2",
        "Query Contacts with Missing Domains",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company_name",
            type: "string",
          },
          { name: "total", customName: "total_missing_domain", type: "number" },
        ],
      ),

      // 3. Clearbit name search API
      n("3", "hitApi", "Search Clearbit for Company Domain", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v1/domains/find?name={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "domain", customName: "clearbit_domain", type: "string" },
            {
              name: "name",
              customName: "clearbit_company_name",
              type: "string",
            },
            { name: "logo", customName: "company_logo_url", type: "string" },
          ],
        },
      }),

      // 4. Google fallback runCode
      n("4", "runCode", "Google Fallback Domain Resolution", 760, {
        runCodeDetails: {
          code: `const clearbitDomain = (variables.clearbit_domain || '').trim();
const companyName = (variables.company_name || '').trim();
const contactEmail = (variables.contact_email || '').trim();
let resolvedDomain = '';
let resolution_method = '';
if (clearbitDomain) {
  resolvedDomain = clearbitDomain;
  resolution_method = 'clearbit';
} else if (contactEmail && contactEmail.includes('@')) {
  const emailDomain = contactEmail.split('@')[1];
  const freeProviders = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','mail.com','protonmail.com'];
  if (!freeProviders.includes(emailDomain.toLowerCase())) {
    resolvedDomain = emailDomain;
    resolution_method = 'email_domain_extraction';
  }
}
if (!resolvedDomain && companyName) {
  const sanitized = companyName.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/inc$|llc$|ltd$|corp$|co$/,'');
  resolvedDomain = sanitized + '.com';
  resolution_method = 'name_inference_needs_verification';
}
output.resolved_domain = resolvedDomain || 'unresolved';
output.resolution_method = resolution_method || 'failed';
output.needs_verification = resolution_method === 'name_inference_needs_verification' ? 'yes' : 'no';`,
          outputVariables: [
            { name: "resolved_domain", type: "string" },
            { name: "resolution_method", type: "string" },
            { name: "needs_verification", type: "string" },
          ],
        },
      }),

      // 5. Domain verification runCode
      n("5", "runCode", "Verify Resolved Domain", 1000, {
        runCodeDetails: {
          code: `const domain = (variables.resolved_domain || '').trim();
const method = (variables.resolution_method || '');
const needsVerify = variables.needs_verification === 'yes';
let verified = false;
let confidence = 'low';
if (domain === 'unresolved') {
  verified = false;
  confidence = 'none';
} else if (method === 'clearbit') {
  verified = true;
  confidence = 'high';
} else if (method === 'email_domain_extraction') {
  verified = true;
  confidence = 'high';
} else if (needsVerify) {
  verified = false;
  confidence = 'low';
} else {
  verified = true;
  confidence = 'medium';
}
output.domain_verified = verified ? 'yes' : 'no';
output.verification_confidence = confidence;
output.final_domain = verified ? domain : '';
output.update_ready = verified ? 'yes' : 'no';`,
          outputVariables: [
            { name: "domain_verified", type: "string" },
            { name: "verification_confidence", type: "string" },
            { name: "final_domain", type: "string" },
            { name: "update_ready", type: "string" },
          ],
        },
      }),

      // 6. CRM update + resolution report HubSpot Sheets
      sheetsNode(
        "6",
        "Log Domain Resolution to Sheets and Update CRM",
        1240,
        "DomainResolution",
        `[["${new Date().toISOString().split("T")[0]}", "{contact_id}", "{contact_first} {contact_last}", "{contact_email}", "{company_name}", "{resolved_domain}", "{resolution_method}", "{domain_verified}", "{verification_confidence}", "{final_domain}"]]`,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1833. MISSING LINKEDIN RESOLUTION (6 nodes)
  // ===================================================================

  "Missing LinkedIn Resolution": {
    summary:
      "Runs daily on a schedule, queries HubSpot for contacts missing LinkedIn profiles, searches Apollo for LinkedIn URLs, falls back to Clearbit for profile discovery, verifies profiles with runCode, and updates CRM records plus logs results to Sheets.",
    nodes: [
      trigger("Schedule: Daily missing LinkedIn resolution", "schedule", {
        schedule: "Every day at 6:30 AM",
        howItStarts:
          "Runs daily to find CRM contacts with missing LinkedIn profile URLs, resolve them via Apollo and Clearbit, verify discovered profiles, and update CRM records",
        setup:
          "Configure HubSpot token for CRM queries and updates. Set Apollo API key for LinkedIn lookups. Configure Clearbit API key for fallback enrichment. Set Google Sheets credentials for resolution logging.",
        inputFields: [],
      }),

      // 2. Missing LinkedIn query HubSpot
      hubspotGetNode(
        "2",
        "Query Contacts with Missing LinkedIn",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "total",
            customName: "total_missing_linkedin",
            type: "number",
          },
        ],
      ),

      // 3. Apollo profile search API
      n("3", "hitApi", "Search Apollo for LinkedIn Profile", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "email", value: "{contact_email}" },
            { key: "first_name", value: "{contact_first}" },
            { key: "last_name", value: "{contact_last}" },
            { key: "organization_name", value: "{company_name}" },
          ],
          variables: [
            {
              name: "person.linkedin_url",
              customName: "apollo_linkedin_url",
              type: "string",
            },
            {
              name: "person.title",
              customName: "apollo_title",
              type: "string",
            },
            {
              name: "person.photo_url",
              customName: "apollo_photo_url",
              type: "string",
            },
          ],
        },
      }),

      // 4. Clearbit fallback API
      n("4", "hitApi", "Clearbit Fallback LinkedIn Lookup", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={contact_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "linkedin.handle",
              customName: "clearbit_linkedin_handle",
              type: "string",
            },
            {
              name: "twitter.handle",
              customName: "clearbit_twitter_handle",
              type: "string",
            },
            {
              name: "employment.title",
              customName: "clearbit_title",
              type: "string",
            },
          ],
        },
      }),

      // 5. Profile verification runCode
      n("5", "runCode", "Verify and Select Best LinkedIn Profile", 760, {
        runCodeDetails: {
          code: `const apolloUrl = (variables.apollo_linkedin_url || '').trim();
const clearbitHandle = (variables.clearbit_linkedin_handle || '').trim();
let finalLinkedin = '';
let source = '';
let confidence = 'low';
if (apolloUrl && apolloUrl.includes('linkedin.com')) {
  finalLinkedin = apolloUrl;
  source = 'apollo';
  confidence = 'high';
} else if (clearbitHandle) {
  finalLinkedin = 'https://linkedin.com/in/' + clearbitHandle;
  source = 'clearbit';
  confidence = 'medium';
}
const nameCheck = (variables.contact_first || '').toLowerCase();
if (finalLinkedin && nameCheck && finalLinkedin.toLowerCase().includes(nameCheck.substring(0, 3))) {
  confidence = 'high';
}
output.final_linkedin_url = finalLinkedin || 'unresolved';
output.linkedin_source = source || 'none';
output.linkedin_confidence = confidence;
output.update_ready = finalLinkedin ? 'yes' : 'no';`,
          outputVariables: [
            { name: "final_linkedin_url", type: "string" },
            { name: "linkedin_source", type: "string" },
            { name: "linkedin_confidence", type: "string" },
            { name: "update_ready", type: "string" },
          ],
        },
      }),

      // 6. CRM LinkedIn update + resolution report
      sheetsNode(
        "6",
        "Log LinkedIn Resolution and Update CRM",
        1000,
        "LinkedInResolution",
        `[["${new Date().toISOString().split("T")[0]}", "{contact_id}", "{contact_first} {contact_last}", "{contact_email}", "{company_name}", "{final_linkedin_url}", "{linkedin_source}", "{linkedin_confidence}", "{update_ready}"]]`,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1834. MULTI-THREADING SUGGESTIONS (7 nodes)
  // ===================================================================

  "Multi-Threading Suggestions": {
    summary:
      "Fires when a deal enters a target stage via webhook, AI-extracts deal context, extracts the deal contact map from HubSpot, AI-analyzes buying committee gaps, discovers missing stakeholders via API, AI-generates engagement strategies, AI-drafts personalized outreach, and updates CRM with a strategy brief sent to the AE via Slack.",
    nodes: [
      trigger("Webhook: Deal stage change event", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal moves to a qualified stage in HubSpot, sending the raw deal property change event with deal ID, stage, pipeline, and associated contacts",
        setup:
          "Configure HubSpot webhook for deal.propertyChange events. Set OpenAI API key for committee analysis and outreach generation. Configure Apollo API for contact discovery. Set Slack webhook for AE briefings.",
        inputFields: [],
      }),

      // 2. AI extract deal context
      aiNode(
        "2",
        "AI Extract Deal Context",
        280,
        "You are a CRM event parser. Extract the deal ID, deal name, deal stage, pipeline name, deal amount, company name, company ID, associated contact emails, deal owner email, deal owner name, and close date from the raw webhook. Return JSON: {deal_id, deal_name, deal_stage, pipeline, deal_amount, company_name, company_id, associated_contacts, deal_owner_email, deal_owner_name, close_date}",
        "Raw deal stage change webhook: {webhook_payload}. Extract all deal context regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_id", as: "company_id", type: "string" },
          {
            field: "associated_contacts",
            as: "associated_contacts",
            type: "string",
          },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "deal_owner_name", as: "deal_owner_name", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
        ],
      ),

      // 3. Deal contact map extraction HubSpot
      hubspotGetNode(
        "3",
        "Extract Deal Contact Map from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}/associations/contacts",
        [
          {
            name: "results[0].id",
            customName: "existing_contact_1_id",
            type: "string",
          },
          {
            name: "results[1].id",
            customName: "existing_contact_2_id",
            type: "string",
          },
          {
            name: "results.length",
            customName: "existing_contact_count",
            type: "number",
          },
        ],
      ),

      // 4. AI buying committee gap analysis
      aiNode(
        "4",
        "AI Analyze Buying Committee Gaps",
        760,
        "You are a B2B deal strategy expert. Analyze the current contacts on this deal versus a typical buying committee for this deal size and stage. Identify missing roles (economic buyer, technical evaluator, champion, coach, blocker, end user). Prioritize which roles to pursue first based on deal stage and amount. Return JSON: {missing_roles, priority_role_1, priority_role_2, committee_coverage_pct, risk_assessment, recommended_approach, typical_committee_size, multi_thread_urgency}",
        "Deal: {deal_name} | Amount: {deal_amount} | Stage: {deal_stage} | Close: {close_date} | Existing contacts: {existing_contact_count} | Associated: {associated_contacts} | Company: {company_name}",
        [
          { field: "missing_roles", as: "missing_roles", type: "string" },
          { field: "priority_role_1", as: "priority_role_1", type: "string" },
          { field: "priority_role_2", as: "priority_role_2", type: "string" },
          {
            field: "committee_coverage_pct",
            as: "committee_coverage_pct",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          {
            field: "multi_thread_urgency",
            as: "multi_thread_urgency",
            type: "string",
          },
        ],
      ),

      // 5. Stakeholder discovery API
      n("5", "hitApi", "Discover Missing Stakeholders via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{company_name}" },
            {
              key: "person_titles",
              value: "{priority_role_1},{priority_role_2}",
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "stakeholder_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "stakeholder_first",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "stakeholder_last",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "stakeholder_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "stakeholder_linkedin",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_stakeholders_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. AI engagement strategy + personalized outreach
      aiNode(
        "6",
        "AI Generate Engagement Strategy and Outreach",
        1240,
        "You are an expert multi-threading strategist. Generate a specific engagement strategy for each missing role, including the best channel (email, LinkedIn, internal intro), messaging angle, and timing. Draft a personalized outreach message for the priority stakeholder that references internal context without being presumptuous. Return JSON: {engagement_strategy, outreach_subject, outreach_body, linkedin_connection_note, internal_intro_request, timing_recommendation, success_metrics}",
        "Stakeholder: {stakeholder_first} {stakeholder_last}, {stakeholder_title} | Company: {company_name} | Deal: {deal_name} ({deal_amount}) | Stage: {deal_stage} | Missing roles: {missing_roles} | Approach: {recommended_approach} | Urgency: {multi_thread_urgency} | Coverage: {committee_coverage_pct}%",
        [
          {
            field: "engagement_strategy",
            as: "engagement_strategy",
            type: "string",
          },
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          {
            field: "linkedin_connection_note",
            as: "linkedin_note",
            type: "string",
          },
          {
            field: "timing_recommendation",
            as: "timing_recommendation",
            type: "string",
          },
        ],
      ),

      // 7. CRM strategy update + AE brief Slack
      slackNode(
        "7",
        "Brief AE on Multi-Threading Strategy",
        1480,
        "Multi-Threading Strategy — *{deal_name}*\\n\\n*Deal:* {deal_amount} | Stage: {deal_stage} | Close: {close_date}\\n*Company:* {company_name}\\n*Committee Coverage:* {committee_coverage_pct}% | Urgency: {multi_thread_urgency}\\n*Risk:* {risk_assessment}\\n\\n*Missing Roles:* {missing_roles}\\n*Priority Stakeholder:* {stakeholder_first} {stakeholder_last} — {stakeholder_title}\\nEmail: {stakeholder_email} | LinkedIn: {stakeholder_linkedin}\\n\\n*Strategy:* {engagement_strategy}\\n*Timing:* {timing_recommendation}\\n*Outreach Subject:* {outreach_subject}\\n*LinkedIn Note:* {linkedin_note}\\n\\nReview and execute multi-threading plan.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1835. MULTI-THREADING WITHIN ACCOUNT (8 nodes)
  // ===================================================================

  "Multi-Threading Within Account": {
    summary:
      "Fires when a target account is flagged via webhook, AI-extracts account details, discovers contacts via Apollo API, classifies contacts by role with runCode, checks existing relationships in HubSpot, AI-generates role-specific messaging, discovers and validates emails, builds send orchestration strategy with runCode, and coordinates dispatch with CRM mapping via email HubSpot and Slack.",
    nodes: [
      trigger("Webhook: Target account flagged for threading", "webhook_app", {
        source: "hubspot",
        event: "company.propertyChange",
        howItStarts:
          "Fires when an account is flagged for multi-threading in HubSpot, sending the raw company property change event with company ID, name, industry, and account tier",
        setup:
          "Configure HubSpot webhook for company.propertyChange events. Set Apollo API key for contact discovery. Set OpenAI API key for role-specific messaging. Configure ZeroBounce for email validation. Set Slack webhook for coordination alerts.",
        inputFields: [],
      }),

      // 2. Target account contact discovery API
      aiNode(
        "2",
        "AI Extract Target Account Details",
        280,
        "You are a CRM event parser. Extract the company ID, company name, industry, employee count, annual revenue, account tier, account owner email, account owner name, target personas, and threading reason from the raw webhook. Return JSON: {company_id, company_name, industry, employee_count, annual_revenue, account_tier, owner_email, owner_name, target_personas, threading_reason}",
        "Raw target account webhook: {webhook_payload}. Extract all account details regardless of field naming.",
        [
          { field: "company_id", as: "company_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "employee_count", as: "employee_count", type: "string" },
          { field: "account_tier", as: "account_tier", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "owner_name", as: "owner_name", type: "string" },
          { field: "target_personas", as: "target_personas", type: "string" },
          { field: "threading_reason", as: "threading_reason", type: "string" },
        ],
      ),

      // 3. Contact discovery API
      n("3", "hitApi", "Discover Account Contacts via Apollo", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{company_name}" },
            { key: "person_titles", value: "{target_personas}" },
            { key: "per_page", value: "10" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "contact_1_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "contact_1_first",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "contact_1_last",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_1_title",
              type: "string",
            },
            {
              name: "people[1].email",
              customName: "contact_2_email",
              type: "string",
            },
            {
              name: "people[1].first_name",
              customName: "contact_2_first",
              type: "string",
            },
            {
              name: "people[1].title",
              customName: "contact_2_title",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 4. Role classification runCode
      n("4", "runCode", "Classify Contacts by Role and Priority", 760, {
        runCodeDetails: {
          code: `const title1 = (variables.contact_1_title || '').toLowerCase();
const title2 = (variables.contact_2_title || '').toLowerCase();
function classifyRole(title) {
  if (title.match(/ceo|coo|cfo|cto|cmo|chief|president/)) return 'executive_buyer';
  if (title.match(/vp|vice president|svp|evp/)) return 'economic_buyer';
  if (title.match(/director|head of/)) return 'decision_maker';
  if (title.match(/manager|lead|senior/)) return 'influencer';
  if (title.match(/engineer|developer|architect|analyst/)) return 'technical_evaluator';
  return 'end_user';
}
function getPriority(role) {
  const priorities = {executive_buyer:1, economic_buyer:2, decision_maker:3, influencer:4, technical_evaluator:5, end_user:6};
  return priorities[role] || 6;
}
const role1 = classifyRole(title1);
const role2 = classifyRole(title2);
const priority1 = getPriority(role1);
const priority2 = getPriority(role2);
output.contact_1_role = role1;
output.contact_2_role = role2;
output.contact_1_priority = priority1;
output.contact_2_priority = priority2;
output.roles_covered = role1 + ', ' + role2;
output.threading_depth = 2;`,
          outputVariables: [
            { name: "contact_1_role", type: "string" },
            { name: "contact_2_role", type: "string" },
            { name: "contact_1_priority", type: "number" },
            { name: "contact_2_priority", type: "number" },
            { name: "roles_covered", type: "string" },
            { name: "threading_depth", type: "number" },
          ],
        },
      }),

      // 5. Existing relationship check HubSpot
      hubspotGetNode(
        "5",
        "Check Existing Relationships in HubSpot",
        1000,
        "crm/v3/objects/contacts/search?query={contact_1_email}",
        [
          {
            name: "total",
            customName: "existing_relationship_count",
            type: "number",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "existing_lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_updated",
            customName: "last_activity_notes",
            type: "string",
          },
        ],
      ),

      // 6. AI role-specific messaging
      aiNode(
        "6",
        "AI Generate Role-Specific Messaging",
        1240,
        "You are an expert account-based marketing strategist. Generate tailored messaging for each identified role within the target account. Each message should reference the specific value proposition relevant to that role, use appropriate tone and technical depth, and create urgency without being pushy. Return JSON: {message_1_subject, message_1_body, message_1_channel, message_2_subject, message_2_body, message_2_channel, sequencing_strategy, cross_reference_talking_points}",
        "Account: {company_name} ({industry}, {employee_count} employees, Tier: {account_tier}) | Contact 1: {contact_1_first} {contact_1_last}, {contact_1_title} (Role: {contact_1_role}) | Contact 2: {contact_2_first}, {contact_2_title} (Role: {contact_2_role}) | Threading reason: {threading_reason} | Existing relationship: {existing_relationship_count} contacts | Roles covered: {roles_covered}",
        [
          {
            field: "message_1_subject",
            as: "message_1_subject",
            type: "string",
          },
          { field: "message_1_body", as: "message_1_body", type: "string" },
          {
            field: "message_2_subject",
            as: "message_2_subject",
            type: "string",
          },
          { field: "message_2_body", as: "message_2_body", type: "string" },
          {
            field: "sequencing_strategy",
            as: "sequencing_strategy",
            type: "string",
          },
          {
            field: "cross_reference_talking_points",
            as: "cross_reference_points",
            type: "string",
          },
        ],
      ),

      // 7. Send orchestration strategy runCode
      n("7", "runCode", "Build Send Orchestration Strategy", 1480, {
        runCodeDetails: {
          code: `const priority1 = parseInt(variables.contact_1_priority) || 6;
const priority2 = parseInt(variables.contact_2_priority) || 6;
const existingCount = parseInt(variables.existing_relationship_count) || 0;
let sendOrder = [];
if (priority1 <= priority2) {
  sendOrder.push({contact: variables.contact_1_first + ' ' + variables.contact_1_last, email: variables.contact_1_email, delay: 0, role: variables.contact_1_role});
  sendOrder.push({contact: variables.contact_2_first, email: variables.contact_2_email, delay: 48, role: variables.contact_2_role});
} else {
  sendOrder.push({contact: variables.contact_2_first, email: variables.contact_2_email, delay: 0, role: variables.contact_2_role});
  sendOrder.push({contact: variables.contact_1_first + ' ' + variables.contact_1_last, email: variables.contact_1_email, delay: 48, role: variables.contact_1_role});
}
output.first_send_contact = sendOrder[0].contact;
output.first_send_email = sendOrder[0].email;
output.first_send_role = sendOrder[0].role;
output.second_send_delay_hrs = sendOrder[1].delay;
output.second_send_contact = sendOrder[1].contact;
output.orchestration_plan = sendOrder.map((s, i) => 'Step ' + (i+1) + ': ' + s.contact + ' (' + s.role + ') — delay ' + s.delay + 'hrs').join('; ');
output.total_threads = sendOrder.length;`,
          outputVariables: [
            { name: "first_send_contact", type: "string" },
            { name: "first_send_email", type: "string" },
            { name: "first_send_role", type: "string" },
            { name: "second_send_delay_hrs", type: "number" },
            { name: "second_send_contact", type: "string" },
            { name: "orchestration_plan", type: "string" },
            { name: "total_threads", type: "number" },
          ],
        },
      }),

      // 8. Coordinated dispatch + CRM map Slack
      slackNode(
        "8",
        "Coordinate Multi-Thread Dispatch and CRM Update",
        1720,
        "Multi-Thread Account Engagement — *{company_name}*\\n\\n*Account Tier:* {account_tier} | Industry: {industry}\\n*Threading Reason:* {threading_reason}\\n*Roles Covered:* {roles_covered}\\n*Threads:* {total_threads}\\n\\n*Orchestration Plan:*\\n{orchestration_plan}\\n\\n*First Send:* {first_send_contact} ({first_send_role}) — {first_send_email}\\nSubject: {message_1_subject}\\n\\n*Cross-Reference Points:* {cross_reference_points}\\n*Sequencing:* {sequencing_strategy}\\n\\nOwner: {owner_name} — review and approve sends.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1836. NPS PROMOTER WARM INTRO PIPELINE (8 nodes)
  // ===================================================================

  "NPS Promoter Warm Intro Pipeline": {
    summary:
      "Fires when an NPS promoter is identified via webhook, AI-analyzes the promoter's network, prioritizes prospects with runCode, AI-generates an intro request, sends outreach to the promoter via CSM email, routes through an introduction facilitation condition, sends warm prospect outreach email, and tracks the referral pipeline in Sheets and HubSpot.",
    nodes: [
      trigger("Webhook: NPS promoter identified", "webhook_app", {
        source: "delighted",
        event: "response.created",
        howItStarts:
          "Fires when a customer submits an NPS response with a score of 9 or 10 (promoter), sending the raw NPS response event with score, comment, respondent details, and survey metadata",
        setup:
          "Configure Delighted webhook for response.created events with promoter filter. Set OpenAI API key for network analysis and intro generation. Set email credentials for CSM outreach. Configure HubSpot token for pipeline tracking. Set Google Sheets for referral logging.",
        inputFields: [],
      }),

      // 2. AI promoter network analysis
      aiNode(
        "2",
        "AI Analyze Promoter Network Potential",
        280,
        "You are an NPS-to-referral strategist. Extract the promoter email, name, company, NPS score, comment, industry, company size, account value, CSM name, CSM email, and product usage data from the raw webhook. Then analyze their likely professional network including peer companies, industry connections, conference circles, and LinkedIn network potential. Return JSON: {promoter_email, promoter_name, promoter_company, nps_score, nps_comment, industry, company_size, account_value, csm_name, csm_email, network_potential, target_companies, intro_likelihood, referral_value_estimate}",
        "Raw NPS promoter webhook: {webhook_payload}. Extract all promoter data and analyze referral network potential.",
        [
          { field: "promoter_email", as: "promoter_email", type: "string" },
          { field: "promoter_name", as: "promoter_name", type: "string" },
          { field: "promoter_company", as: "promoter_company", type: "string" },
          { field: "nps_score", as: "nps_score", type: "string" },
          { field: "nps_comment", as: "nps_comment", type: "string" },
          { field: "industry", as: "promoter_industry", type: "string" },
          { field: "csm_name", as: "csm_name", type: "string" },
          { field: "csm_email", as: "csm_email", type: "string" },
          { field: "target_companies", as: "target_companies", type: "string" },
          { field: "intro_likelihood", as: "intro_likelihood", type: "string" },
          {
            field: "referral_value_estimate",
            as: "referral_value_estimate",
            type: "string",
          },
        ],
      ),

      // 3. Prospect prioritization runCode
      n("3", "runCode", "Prioritize Referral Prospects", 520, {
        runCodeDetails: {
          code: `const targetCompanies = (variables.target_companies || '').split(',').map(c => c.trim()).filter(Boolean);
const introLikelihood = (variables.intro_likelihood || '').toLowerCase();
const npsScore = parseInt(variables.nps_score) || 0;
const refValue = (variables.referral_value_estimate || '').toLowerCase();
let priority = 'low';
if (npsScore === 10 && introLikelihood.includes('high')) priority = 'high';
else if (npsScore >= 9 && !introLikelihood.includes('low')) priority = 'medium';
let topProspect = targetCompanies[0] || 'General referral request';
let prospectCount = targetCompanies.length;
let introApproach = '';
if (priority === 'high') {
  introApproach = 'Direct ask with specific company name — promoter is highly engaged';
} else if (priority === 'medium') {
  introApproach = 'Soft ask with industry framing — promoter is positive but may need incentive';
} else {
  introApproach = 'General referral program mention — low-pressure approach';
}
output.referral_priority = priority;
output.top_prospect_company = topProspect;
output.prospect_count = prospectCount;
output.intro_approach = introApproach;
output.ask_specificity = priority === 'high' ? 'specific_company' : 'general_industry';`,
          outputVariables: [
            { name: "referral_priority", type: "string" },
            { name: "top_prospect_company", type: "string" },
            { name: "prospect_count", type: "number" },
            { name: "intro_approach", type: "string" },
            { name: "ask_specificity", type: "string" },
          ],
        },
      }),

      // 4. AI intro request generation
      aiNode(
        "4",
        "AI Generate Introduction Request",
        760,
        "You are a customer success and referral expert. Craft a warm, genuine introduction request email from the CSM to the NPS promoter. Reference their positive feedback, acknowledge their success with the product, and make a specific but non-pressuring referral ask. Include a referral incentive mention if appropriate. Return JSON: {intro_request_subject, intro_request_body, incentive_mention, follow_up_timing, ask_framing}",
        "Promoter: {promoter_name} at {promoter_company} | NPS: {nps_score} | Comment: {nps_comment} | CSM: {csm_name} | Priority: {referral_priority} | Top prospect: {top_prospect_company} | Approach: {intro_approach} | Ask type: {ask_specificity} | Industry: {promoter_industry}",
        [
          {
            field: "intro_request_subject",
            as: "intro_request_subject",
            type: "string",
          },
          {
            field: "intro_request_body",
            as: "intro_request_body",
            type: "string",
          },
          {
            field: "incentive_mention",
            as: "incentive_mention",
            type: "string",
          },
          { field: "follow_up_timing", as: "follow_up_timing", type: "string" },
          { field: "ask_framing", as: "ask_framing", type: "string" },
        ],
      ),

      // 5. Promoter outreach via CSM email
      emailNode(
        "5",
        "Send Introduction Request to Promoter",
        1000,
        "{promoter_email}",
        "{intro_request_subject}",
        "{intro_request_body}",
      ),

      // 6. Introduction facilitation condition
      n(
        "6",
        "condition",
        "Check if referral priority warrants direct outreach",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "referral_priority",
                operator: "equals",
                value: "high",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Warm prospect outreach email (output_1 of condition)
      emailNode(
        "7",
        "Send Warm Prospect Introduction Email",
        1480,
        "{csm_email}",
        "Warm intro opportunity — {top_prospect_company} via {promoter_name}",
        "Hi {csm_name},\\n\\nThe introduction request to {promoter_name} at {promoter_company} has been sent (NPS: {nps_score}).\\n\\nTarget prospect: {top_prospect_company}\\nAsk framing: {ask_framing}\\nIncentive: {incentive_mention}\\nExpected follow-up: {follow_up_timing}\\n\\nIf {promoter_name} responds positively, please facilitate the introduction and update the referral pipeline.\\n\\nReferral priority: {referral_priority}\\nProspect count: {prospect_count}",
      ),

      // 8. Referral pipeline tracking Sheets HubSpot
      sheetsNode(
        "8",
        "Track Referral in Pipeline",
        1720,
        "ReferralPipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{promoter_name}", "{promoter_company}", "{nps_score}", "{referral_priority}", "{top_prospect_company}", "{intro_approach}", "{ask_specificity}", "{referral_value_estimate}", "intro_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1837. NAMED ACCOUNT PROTECTION GATE (6 nodes)
  // ===================================================================

  "Named Account Protection Gate": {
    summary:
      "Fires when a pre-sequence check triggers via webhook, AI-extracts contact data, looks up the protected account list with runCode, gates the contact through a protection condition, routes blocked contacts via Slack, alerts the account owner via Slack, and logs the protection event to Sheets.",
    nodes: [
      trigger("Webhook: Pre-sequence protection check", "webhook_app", {
        source: "outreach",
        event: "sequence.preEnroll",
        howItStarts:
          "Fires before a contact is enrolled in an outbound sequence, sending the raw pre-enrollment event with contact email, sequence name, SDR details, and enrollment metadata",
        setup:
          "Configure Outreach webhook for sequence.preEnroll events. Set OpenAI API key for contact extraction. Maintain a protected accounts list in environment variables or database. Configure Slack webhook for block alerts and owner notifications. Set Google Sheets for audit logging.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI Extract Pre-Sequence Contact Data",
        280,
        "You are a sequence enrollment event parser. Extract the contact email, contact name, contact company, contact domain, sequence name, sequence ID, SDR email, SDR name, enrollment source, and enrollment timestamp from the raw pre-enrollment webhook. Return JSON: {contact_email, contact_name, contact_company, contact_domain, sequence_name, sequence_id, sdr_email, sdr_name, enrollment_source, enrollment_timestamp}",
        "Raw pre-sequence webhook: {webhook_payload}. Extract all enrollment data regardless of field naming conventions.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_company", as: "contact_company", type: "string" },
          { field: "contact_domain", as: "contact_domain", type: "string" },
          { field: "sequence_name", as: "sequence_name", type: "string" },
          { field: "sdr_email", as: "sdr_email", type: "string" },
          { field: "sdr_name", as: "sdr_name", type: "string" },
        ],
      ),

      // 3. Protected list lookup runCode
      n("3", "runCode", "Check Against Protected Account List", 520, {
        runCodeDetails: {
          code: `const contactDomain = (variables.contact_domain || '').toLowerCase().trim();
const contactCompany = (variables.contact_company || '').toLowerCase().trim();
const protectedDomains = ['acme.com','bigcorp.io','enterprise.com','megaclient.com','strategicpartner.com','topaccount.com','vipcompany.com','keypartner.io'];
const protectedCompanies = ['acme inc','bigcorp','enterprise solutions','megaclient','strategic partner','top account','vip company','key partner'];
let isProtected = false;
let protectionReason = '';
let accountOwner = '';
if (protectedDomains.includes(contactDomain)) {
  isProtected = true;
  protectionReason = 'Domain ' + contactDomain + ' is on the protected accounts list';
  accountOwner = 'ae-team@company.com';
} else if (protectedCompanies.some(pc => contactCompany.includes(pc))) {
  isProtected = true;
  protectionReason = 'Company ' + contactCompany + ' matches a protected account name';
  accountOwner = 'ae-team@company.com';
}
output.is_protected = isProtected ? 'yes' : 'no';
output.protection_reason = protectionReason || 'Not protected — clear to enroll';
output.account_owner_email = accountOwner || '';
output.protection_status = isProtected ? 'BLOCKED' : 'CLEARED';`,
          outputVariables: [
            { name: "is_protected", type: "string" },
            { name: "protection_reason", type: "string" },
            { name: "account_owner_email", type: "string" },
            { name: "protection_status", type: "string" },
          ],
        },
      }),

      // 4. Protection gate condition
      n("4", "condition", "Is contact on a protected account?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_protected", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Blocked contact routing + account owner alert Slack (output_1 = protected)
      slackNode(
        "5",
        "Alert: Blocked Enrollment on Protected Account",
        1000,
        "PROTECTED ACCOUNT BLOCK\\n\\n*Contact:* {contact_name} ({contact_email})\\n*Company:* {contact_company} ({contact_domain})\\n*Sequence:* {sequence_name}\\n*SDR:* {sdr_name} ({sdr_email})\\n\\n*Status:* {protection_status}\\n*Reason:* {protection_reason}\\n*Account Owner:* {account_owner_email}\\n\\nThis enrollment has been BLOCKED. Contact the account owner before reaching out to anyone at this account.",
      ),

      // 6. Protection audit log Sheets (output_2 = not protected, just log clearance)
      sheetsNode(
        "6",
        "Log Protection Check to Audit Trail",
        1000,
        "ProtectionAudit",
        `[["${new Date().toISOString().split("T")[0]}", "{contact_email}", "{contact_name}", "{contact_company}", "{contact_domain}", "{sequence_name}", "{sdr_name}", "{protection_status}", "{protection_reason}", "{account_owner_email}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
    ],
  },

  // ===================================================================
  // 1838. NEGATIVE SENTIMENT DISPLACEMENT PITCH (8 nodes)
  // ===================================================================

  "Negative Sentiment Displacement Pitch": {
    summary:
      "Runs daily on a schedule, monitors competitor reviews for negative sentiment via API, scores severity with runCode, identifies the reviewer's company with runCode, qualifies the company via Clearbit, gates on displacement scoring condition, discovers contacts via API, and AI-drafts a displacement pitch delivered via email with HubSpot CRM intel tracking.",
    nodes: [
      trigger("Schedule: Daily negative sentiment monitor", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to monitor competitor product reviews across G2, Capterra, and TrustRadius for negative sentiment signals that indicate displacement opportunities",
        setup:
          "Configure review monitoring API credentials (G2 or Capterra API). Set OpenAI API key for sentiment analysis and pitch generation. Configure Clearbit for company qualification. Set Apollo API for contact discovery. Configure HubSpot token for CRM tracking.",
        inputFields: [],
      }),

      // 2. Negative sentiment monitoring API
      n(
        "2",
        "hitApi",
        "Monitor Competitor Reviews for Negative Sentiment",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://data.g2.com/api/v1/survey-responses?filter[product_name]={competitor_product}&filter[star_rating][lt]=3&filter[updated_at][gt]={last_run_date}",
            headers: [
              { key: "Authorization", value: "Token token={g2_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.title",
                customName: "review_title",
                type: "string",
              },
              {
                name: "data[0].attributes.comment_answers.love",
                customName: "review_positive",
                type: "string",
              },
              {
                name: "data[0].attributes.comment_answers.hate",
                customName: "review_negative",
                type: "string",
              },
              {
                name: "data[0].attributes.star_rating",
                customName: "review_rating",
                type: "string",
              },
              {
                name: "data[0].attributes.submitted_at",
                customName: "review_date",
                type: "string",
              },
              {
                name: "data[0].attributes.secondary_answers.company_name",
                customName: "reviewer_company",
                type: "string",
              },
              {
                name: "data[0].attributes.secondary_answers.company_size",
                customName: "reviewer_company_size",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Severity scoring runCode
      n("3", "runCode", "Score Review Severity and Extract Pain Points", 520, {
        runCodeDetails: {
          code: `const negative = (variables.review_negative || '').toLowerCase();
const rating = parseInt(variables.review_rating) || 3;
let severity = 0;
let painPoints = [];
const criticalKeywords = ['terrible','awful','broken','unusable','worst','nightmare','hate','disaster'];
const highKeywords = ['slow','buggy','expensive','missing','frustrating','poor','difficult','unreliable'];
const medKeywords = ['okay','average','lacking','wish','limited','basic','decent'];
criticalKeywords.forEach(kw => { if (negative.includes(kw)) { severity += 3; painPoints.push(kw); }});
highKeywords.forEach(kw => { if (negative.includes(kw)) { severity += 2; painPoints.push(kw); }});
medKeywords.forEach(kw => { if (negative.includes(kw)) { severity += 1; painPoints.push(kw); }});
if (rating === 1) severity += 3;
else if (rating === 2) severity += 1;
let severityLevel = severity >= 8 ? 'critical' : severity >= 5 ? 'high' : severity >= 3 ? 'medium' : 'low';
output.severity_score = severity;
output.severity_level = severityLevel;
output.pain_points = painPoints.join(', ') || 'general dissatisfaction';
output.displacement_opportunity = severity >= 5 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "severity_score", type: "number" },
            { name: "severity_level", type: "string" },
            { name: "pain_points", type: "string" },
            { name: "displacement_opportunity", type: "string" },
          ],
        },
      }),

      // 4. Reviewer company identification runCode
      n("4", "runCode", "Identify Reviewer Company Details", 760, {
        runCodeDetails: {
          code: `const company = (variables.reviewer_company || '').trim();
const companySize = (variables.reviewer_company_size || '').trim();
let companyDomain = '';
if (company) {
  const sanitized = company.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/inc$|llc$|ltd$|corp$/,'');
  companyDomain = sanitized + '.com';
}
let sizeCategory = 'unknown';
if (companySize.includes('1000') || companySize.includes('enterprise') || companySize.includes('5000')) sizeCategory = 'enterprise';
else if (companySize.includes('500') || companySize.includes('mid')) sizeCategory = 'mid_market';
else if (companySize.includes('100') || companySize.includes('200')) sizeCategory = 'smb_plus';
else sizeCategory = 'smb';
output.reviewer_domain = companyDomain || 'unknown';
output.size_category = sizeCategory;
output.company_identified = company ? 'yes' : 'no';`,
          outputVariables: [
            { name: "reviewer_domain", type: "string" },
            { name: "size_category", type: "string" },
            { name: "company_identified", type: "string" },
          ],
        },
      }),

      // 5. Company qualification Clearbit API
      n("5", "hitApi", "Qualify Reviewer Company via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={reviewer_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "qualified_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "estimated_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      // 6. Displacement scoring condition
      n("6", "condition", "Is displacement opportunity viable?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "displacement_opportunity",
              operator: "equals",
              value: "yes",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Contact discovery API (output_1)
      n("7", "hitApi", "Discover Decision Maker Contacts via Apollo", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{qualified_company_name}" },
            {
              key: "person_titles",
              value: '["VP","Director","Head of","Manager"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            { name: "people[0].email", customName: "dm_email", type: "string" },
            {
              name: "people[0].first_name",
              customName: "dm_first_name",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "dm_last_name",
              type: "string",
            },
            { name: "people[0].title", customName: "dm_title", type: "string" },
          ],
        },
      }),

      // 8. AI displacement draft + CRM intel email HubSpot
      hubspotPostNode(
        "8",
        "Create Displacement Lead in HubSpot CRM",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{dm_email}","firstname":"{dm_first_name}","lastname":"{dm_last_name}","jobtitle":"{dm_title}","company":"{qualified_company_name}","hs_lead_status":"NEW","notes_last_updated":"Displacement opportunity from competitor review. Severity: {severity_level} ({severity_score}). Pain points: {pain_points}. Review: {review_title}. Rating: {review_rating}/5. Industry: {company_industry}. Employees: {employee_count}. Revenue: {estimated_revenue}."}',
          },
        ],
        [
          { name: "id", customName: "displacement_lead_id", type: "string" },
          {
            name: "properties.email",
            customName: "lead_email_created",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1839. NEW-HIRE TRIGGER OUTREACH (7 nodes)
  // ===================================================================

  "New-Hire Trigger Outreach": {
    summary:
      "Fires when a new hire is detected via webhook, enriches the profile via Clearbit API, AI-assesses the onboarding window, AI-drafts a welcome message, resolves and validates the email via API, schedules a timed delay send, and updates HubSpot CRM with a trigger log.",
    nodes: [
      trigger("Webhook: New hire detected", "webhook_app", {
        source: "linkedin",
        event: "job_change.detected",
        howItStarts:
          "Fires when a new hire or job change is detected via LinkedIn Sales Navigator or similar monitoring tool, sending the raw job change event with person details, new company, and role information",
        setup:
          "Configure LinkedIn Sales Navigator webhook or job change monitoring API. Set Clearbit API key for profile enrichment. Set OpenAI API key for onboarding assessment and message generation. Configure ZeroBounce for email validation. Set HubSpot token for CRM tracking.",
        inputFields: [],
      }),

      // 2. Profile enrichment Clearbit API
      aiNode(
        "2",
        "AI Extract New Hire Event Data",
        280,
        "You are a job change event parser. Extract the person's full name, first name, last name, previous company, previous title, new company, new title, new company domain, LinkedIn URL, start date estimate, and change type from the raw webhook. Return JSON: {full_name, first_name, last_name, previous_company, previous_title, new_company, new_title, new_company_domain, linkedin_url, start_date, change_type}",
        "Raw new hire detection webhook: {webhook_payload}. Extract all person and job change details regardless of field formats.",
        [
          { field: "first_name", as: "hire_first_name", type: "string" },
          { field: "last_name", as: "hire_last_name", type: "string" },
          { field: "previous_company", as: "previous_company", type: "string" },
          { field: "previous_title", as: "previous_title", type: "string" },
          { field: "new_company", as: "new_company", type: "string" },
          { field: "new_title", as: "new_title", type: "string" },
          {
            field: "new_company_domain",
            as: "new_company_domain",
            type: "string",
          },
          { field: "linkedin_url", as: "hire_linkedin_url", type: "string" },
          { field: "start_date", as: "hire_start_date", type: "string" },
        ],
      ),

      // 3. Clearbit enrichment
      n("3", "hitApi", "Enrich New Hire Profile via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={new_company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 4. AI onboarding window assessment
      aiNode(
        "4",
        "AI Assess Onboarding Window Timing",
        760,
        "You are a sales timing expert. Assess the optimal outreach window for a new hire based on their role seniority, company size, industry norms, and start date. Senior leaders have a 30-90 day window to make tech decisions. Determine the best outreach timing, messaging angle, and urgency. Return JSON: {optimal_outreach_day, outreach_window_days, messaging_angle, urgency_level, role_seniority, buying_authority, welcome_subject, welcome_body}",
        "New hire: {hire_first_name} {hire_last_name} | New role: {new_title} at {new_company} | Previous: {previous_title} at {previous_company} | Start: {hire_start_date} | Company size: {company_employees} | Industry: {company_industry} | Revenue: {company_revenue} | Tech stack: {company_tech_stack}",
        [
          {
            field: "optimal_outreach_day",
            as: "optimal_outreach_day",
            type: "string",
          },
          {
            field: "outreach_window_days",
            as: "outreach_window_days",
            type: "string",
          },
          { field: "messaging_angle", as: "messaging_angle", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "role_seniority", as: "role_seniority", type: "string" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
        ],
      ),

      // 5. Email resolution + validation API
      n("5", "hitApi", "Resolve and Validate New Hire Email", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "first_name", value: "{hire_first_name}" },
            { key: "last_name", value: "{hire_last_name}" },
            { key: "organization_name", value: "{new_company}" },
            { key: "domain", value: "{new_company_domain}" },
          ],
          variables: [
            { name: "person.email", customName: "hire_email", type: "string" },
            {
              name: "person.email_status",
              customName: "email_status",
              type: "string",
            },
            {
              name: "person.phone_numbers[0].raw_number",
              customName: "hire_phone",
              type: "string",
            },
          ],
        },
      }),

      // 6. Timed send delay
      n("6", "delayAction", "Delay Send Until Optimal Outreach Window", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait {optimal_outreach_day} days for the new hire to settle in before outreach — timing based on role seniority ({role_seniority}) and onboarding window ({outreach_window_days} days)",
        },
      }),

      // 7. CRM update + trigger log HubSpot
      hubspotPostNode(
        "7",
        "Log New-Hire Trigger and Update CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{hire_email}","firstname":"{hire_first_name}","lastname":"{hire_last_name}","jobtitle":"{new_title}","company":"{new_company}","hs_lead_status":"OPEN","notes_last_updated":"New-hire trigger: {hire_first_name} {hire_last_name} joined {new_company} as {new_title} (previously {previous_title} at {previous_company}). Seniority: {role_seniority}. Messaging: {messaging_angle}. Urgency: {urgency_level}. Outreach window: {outreach_window_days} days. LinkedIn: {hire_linkedin_url}."}',
          },
        ],
        [
          { name: "id", customName: "new_hire_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1840. NEWSLETTER CLICK INTENT HANDOFF (8 nodes)
  // ===================================================================

  "Newsletter Click Intent Handoff": {
    summary:
      "Fires when a newsletter click event triggers via webhook, AI-extracts click data, analyzes click patterns with runCode, enriches the subscriber via Clearbit API, scores intent with runCode, gates on an intent threshold condition, AI-generates a handoff package, creates a lead in HubSpot, and alerts the SDR via Slack.",
    nodes: [
      trigger("Webhook: Newsletter click event", "webhook_app", {
        source: "mailchimp",
        event: "subscribe.click",
        howItStarts:
          "Fires when a newsletter subscriber clicks a link in a campaign, sending the raw click event with subscriber email, link URL, campaign details, and engagement metadata",
        setup:
          "Configure Mailchimp webhook for click events. Set OpenAI API key for intent analysis and handoff generation. Configure Clearbit API for subscriber enrichment. Set HubSpot token for lead creation. Set Slack webhook for SDR alerts.",
        inputFields: [],
      }),

      // 2. AI extract click data
      aiNode(
        "2",
        "AI Extract Newsletter Click Data",
        280,
        "You are a marketing event parser. Extract the subscriber email, subscriber name, clicked URL, click timestamp, campaign name, campaign subject, list name, total campaign clicks, link category, and any UTM parameters from the raw click webhook. Return JSON: {subscriber_email, subscriber_name, clicked_url, click_timestamp, campaign_name, campaign_subject, list_name, total_clicks, link_category, utm_source, utm_medium, utm_campaign}",
        "Raw newsletter click webhook: {webhook_payload}. Extract all click event data regardless of field naming conventions.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "clicked_url", as: "clicked_url", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "campaign_subject", as: "campaign_subject", type: "string" },
          { field: "total_clicks", as: "total_clicks", type: "string" },
          { field: "link_category", as: "link_category", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
        ],
      ),

      // 3. Click pattern analysis runCode
      n("3", "runCode", "Analyze Click Patterns for Intent Signals", 520, {
        runCodeDetails: {
          code: `const clickedUrl = (variables.clicked_url || '').toLowerCase();
const totalClicks = parseInt(variables.total_clicks) || 1;
const linkCategory = (variables.link_category || '').toLowerCase();
let intentSignals = [];
let contentInterest = '';
if (clickedUrl.includes('pricing') || clickedUrl.includes('plans')) {
  intentSignals.push('pricing_interest');
  contentInterest = 'pricing';
}
if (clickedUrl.includes('demo') || clickedUrl.includes('trial') || clickedUrl.includes('signup')) {
  intentSignals.push('product_interest');
  contentInterest = 'product_evaluation';
}
if (clickedUrl.includes('case-study') || clickedUrl.includes('customer') || clickedUrl.includes('testimonial')) {
  intentSignals.push('social_proof_seeking');
  contentInterest = 'validation';
}
if (clickedUrl.includes('comparison') || clickedUrl.includes('vs') || clickedUrl.includes('alternative')) {
  intentSignals.push('competitor_research');
  contentInterest = 'competitive_evaluation';
}
if (clickedUrl.includes('integration') || clickedUrl.includes('api') || clickedUrl.includes('docs')) {
  intentSignals.push('technical_evaluation');
  contentInterest = 'technical_fit';
}
if (!contentInterest) contentInterest = 'general_engagement';
let engagementLevel = totalClicks >= 5 ? 'high' : totalClicks >= 3 ? 'medium' : 'low';
output.intent_signals = intentSignals.join(', ') || 'general_engagement';
output.content_interest = contentInterest;
output.engagement_level = engagementLevel;
output.click_count = totalClicks;
output.signal_strength = intentSignals.length;`,
          outputVariables: [
            { name: "intent_signals", type: "string" },
            { name: "content_interest", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "click_count", type: "number" },
            { name: "signal_strength", type: "number" },
          ],
        },
      }),

      // 4. Subscriber enrichment Clearbit API
      n("4", "hitApi", "Enrich Subscriber via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={subscriber_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "employment.name",
              customName: "subscriber_company",
              type: "string",
            },
            {
              name: "employment.title",
              customName: "subscriber_title",
              type: "string",
            },
            {
              name: "employment.role",
              customName: "subscriber_role",
              type: "string",
            },
            {
              name: "employment.seniority",
              customName: "subscriber_seniority",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "subscriber_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 5. Intent scoring runCode
      n("5", "runCode", "Calculate Intent Score for Handoff", 1000, {
        runCodeDetails: {
          code: `const signalStrength = parseInt(variables.signal_strength) || 0;
const engagementLevel = (variables.engagement_level || '').toLowerCase();
const seniority = (variables.subscriber_seniority || '').toLowerCase();
const role = (variables.subscriber_role || '').toLowerCase();
let intentScore = 0;
intentScore += signalStrength * 15;
if (engagementLevel === 'high') intentScore += 30;
else if (engagementLevel === 'medium') intentScore += 15;
if (seniority === 'executive' || seniority === 'director') intentScore += 25;
else if (seniority === 'manager' || seniority === 'vp') intentScore += 20;
else if (seniority === 'senior') intentScore += 10;
if (role === 'engineering' || role === 'product' || role === 'operations') intentScore += 10;
let handoffReady = intentScore >= 50 ? 'yes' : 'no';
let handoffTier = intentScore >= 75 ? 'hot' : intentScore >= 50 ? 'warm' : 'nurture';
output.intent_score = intentScore;
output.handoff_ready = handoffReady;
output.handoff_tier = handoffTier;
output.scoring_breakdown = 'Signals:' + (signalStrength * 15) + ' Engagement:' + (engagementLevel === 'high' ? 30 : 15) + ' Seniority:' + (seniority === 'executive' ? 25 : 10) + ' Role:' + (role === 'engineering' ? 10 : 0);`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "handoff_ready", type: "string" },
            { name: "handoff_tier", type: "string" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Threshold gate condition
      n("6", "condition", "Is intent score above handoff threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "handoff_ready", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. CRM lead creation + assignment HubSpot (output_1)
      hubspotPostNode(
        "7",
        "Create Intent-Based Lead in HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{subscriber_email}","firstname":"{enriched_name}","jobtitle":"{subscriber_title}","company":"{subscriber_company}","hs_lead_status":"NEW","notes_last_updated":"Newsletter intent handoff — Score: {intent_score} ({handoff_tier}). Signals: {intent_signals}. Content interest: {content_interest}. Campaign: {campaign_name}. Clicks: {click_count}. Seniority: {subscriber_seniority}. LinkedIn: {subscriber_linkedin}. Scoring: {scoring_breakdown}."}',
          },
        ],
        [
          { name: "id", customName: "intent_lead_id", type: "string" },
          {
            name: "properties.email",
            customName: "lead_email",
            type: "string",
          },
        ],
      ),

      // 8. SDR alert + handoff Slack (output_2 goes to nurture log, but we chain 7->8)
      slackNode(
        "8",
        "Alert SDR of Newsletter Intent Lead",
        1720,
        "Newsletter Intent Lead Handoff\\n\\n*Subscriber:* {enriched_name} ({subscriber_email})\\n*Company:* {subscriber_company} | Title: {subscriber_title}\\n*Seniority:* {subscriber_seniority}\\n\\n*Intent Score:* {intent_score} | Tier: {handoff_tier}\\n*Signals:* {intent_signals}\\n*Content Interest:* {content_interest}\\n*Engagement:* {engagement_level} ({click_count} clicks)\\n\\n*Campaign:* {campaign_name}\\n*Clicked:* {clicked_url}\\n*LinkedIn:* {subscriber_linkedin}\\n\\n*Scoring:* {scoring_breakdown}\\n\\nFollow up within 24 hours — high intent detected.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1841. NEWSLETTER SPONSORSHIP PITCH (7 nodes)
  // ===================================================================

  "Newsletter Sponsorship Pitch": {
    summary:
      "Runs weekly on a schedule, AI-researches industry newsletters, AI-analyzes audience fit, checks content relevance with runCode, discovers newsletter contacts via API, validates emails, and AI-drafts a sponsorship pitch with tracker logging to Sheets.",
    nodes: [
      trigger("Schedule: Weekly newsletter sponsorship research", "schedule", {
        schedule: "Every Tuesday at 9:00 AM",
        howItStarts:
          "Runs weekly to research industry newsletters with overlapping audiences, analyze sponsorship potential, and generate personalized pitch outreach to newsletter operators",
        setup:
          "Configure OpenAI API key for newsletter research and pitch generation. Set Apollo API key for contact discovery. Configure ZeroBounce for email validation. Set Google Sheets credentials for sponsorship tracking.",
        inputFields: [],
      }),

      // 2. AI newsletter research
      aiNode(
        "2",
        "AI Research Industry Newsletters",
        280,
        "You are a newsletter sponsorship researcher. Identify newsletters in the target industry with engaged audiences that overlap with our ICP. Evaluate subscriber count estimates, open rates, engagement quality, sponsorship pricing, ad format options, and past sponsor quality. Return JSON: {newsletter_name, newsletter_url, publisher_name, publisher_company, estimated_subscribers, estimated_open_rate, audience_overlap_score, sponsorship_price_range, ad_formats, past_sponsors, audience_demographics, content_focus}",
        "Research newsletters in the B2B SaaS and technology space that reach decision makers in our target market. Focus on newsletters with 5000+ subscribers and consistent publishing schedules.",
        [
          { field: "newsletter_name", as: "newsletter_name", type: "string" },
          { field: "newsletter_url", as: "newsletter_url", type: "string" },
          { field: "publisher_name", as: "publisher_name", type: "string" },
          {
            field: "publisher_company",
            as: "publisher_company",
            type: "string",
          },
          {
            field: "estimated_subscribers",
            as: "estimated_subscribers",
            type: "string",
          },
          {
            field: "estimated_open_rate",
            as: "estimated_open_rate",
            type: "string",
          },
          {
            field: "audience_overlap_score",
            as: "audience_overlap_score",
            type: "string",
          },
          {
            field: "sponsorship_price_range",
            as: "sponsorship_price_range",
            type: "string",
          },
          { field: "ad_formats", as: "ad_formats", type: "string" },
          { field: "content_focus", as: "content_focus", type: "string" },
        ],
      ),

      // 3. AI audience fit analysis
      aiNode(
        "3",
        "AI Analyze Audience Fit for Sponsorship",
        520,
        "You are a media buying strategist. Evaluate the newsletter's audience against our ICP (B2B SaaS decision makers, 50-5000 employees). Calculate estimated CPM, projected click-through rates, and expected lead generation. Determine the best ad format and messaging approach. Return JSON: {audience_fit_score, estimated_cpm, projected_ctr, expected_leads_per_issue, best_ad_format, messaging_approach, roi_estimate, sponsorship_recommendation, negotiation_leverage, optimal_budget}",
        "Newsletter: {newsletter_name} by {publisher_name} | Subscribers: {estimated_subscribers} | Open rate: {estimated_open_rate} | Overlap: {audience_overlap_score} | Price: {sponsorship_price_range} | Formats: {ad_formats} | Content: {content_focus}",
        [
          {
            field: "audience_fit_score",
            as: "audience_fit_score",
            type: "string",
          },
          { field: "estimated_cpm", as: "estimated_cpm", type: "string" },
          {
            field: "expected_leads_per_issue",
            as: "expected_leads",
            type: "string",
          },
          { field: "best_ad_format", as: "best_ad_format", type: "string" },
          {
            field: "messaging_approach",
            as: "messaging_approach",
            type: "string",
          },
          { field: "roi_estimate", as: "roi_estimate", type: "string" },
          {
            field: "sponsorship_recommendation",
            as: "sponsorship_recommendation",
            type: "string",
          },
          { field: "optimal_budget", as: "optimal_budget", type: "string" },
        ],
      ),

      // 4. Content relevance check runCode
      n("4", "runCode", "Verify Content Relevance and Budget Fit", 760, {
        runCodeDetails: {
          code: `const fitScore = parseInt(variables.audience_fit_score) || 0;
const recommendation = (variables.sponsorship_recommendation || '').toLowerCase();
const budget = (variables.optimal_budget || '').replace(/[^0-9]/g, '');
const budgetNum = parseInt(budget) || 0;
const maxBudget = 5000;
let approved = false;
let reason = '';
if (fitScore >= 70 && budgetNum <= maxBudget && recommendation.includes('recommend')) {
  approved = true;
  reason = 'High audience fit (' + fitScore + '%), within budget ($' + budgetNum + '), recommended by analysis';
} else if (fitScore >= 50 && budgetNum <= maxBudget) {
  approved = true;
  reason = 'Moderate audience fit (' + fitScore + '%), within budget — worth testing';
} else if (budgetNum > maxBudget) {
  approved = false;
  reason = 'Budget ($' + budgetNum + ') exceeds max ($' + maxBudget + ')';
} else {
  approved = false;
  reason = 'Low audience fit (' + fitScore + '%) — not recommended at this time';
}
output.pitch_approved = approved ? 'yes' : 'no';
output.approval_reason = reason;
output.budget_status = budgetNum <= maxBudget ? 'within_budget' : 'over_budget';`,
          outputVariables: [
            { name: "pitch_approved", type: "string" },
            { name: "approval_reason", type: "string" },
            { name: "budget_status", type: "string" },
          ],
        },
      }),

      // 5. Contact discovery API
      n("5", "hitApi", "Discover Newsletter Contact via Apollo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{publisher_company}" },
            {
              key: "person_titles",
              value:
                '["Editor","Publisher","Founder","Head of Content","Newsletter Manager"]',
            },
            { key: "per_page", value: "3" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "publisher_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "publisher_first",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "publisher_last",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "publisher_title",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Publisher Email Address", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={publisher_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_valid_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI sponsorship pitch draft + tracker Sheets
      sheetsNode(
        "7",
        "Log Sponsorship Pitch to Tracker",
        1480,
        "SponsorshipTracker",
        `[["${new Date().toISOString().split("T")[0]}", "{newsletter_name}", "{publisher_name}", "{publisher_email}", "{estimated_subscribers}", "{audience_fit_score}", "{sponsorship_price_range}", "{optimal_budget}", "{best_ad_format}", "{roi_estimate}", "{pitch_approved}", "{approval_reason}", "{email_valid_status}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1842. NICHE FORUM QUESTION TRIGGER (8 nodes)
  // ===================================================================

  "Niche Forum Question Trigger": {
    summary:
      "Runs daily on a schedule, monitors niche forums for relevant questions via API, filters for relevance with runCode, AI-generates helpful answers, identifies the commenter's company with runCode, gates on ICP qualification condition, routes through an approval gate for forum response posting, and sends direct outreach with HubSpot lead creation via email.",
    nodes: [
      trigger("Schedule: Daily niche forum monitoring", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to monitor niche industry forums (Stack Overflow, Reddit, Quora, industry-specific forums) for questions related to problems our product solves, identifying engagement and lead generation opportunities",
        setup:
          "Configure forum monitoring API credentials (Apify or custom scraper). Set OpenAI API key for answer generation and company identification. Configure HubSpot token for lead creation. Set Slack webhook for approval routing.",
        inputFields: [],
      }),

      // 2. Forum monitoring API
      n("2", "hitApi", "Monitor Forum for Relevant Questions", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apify.com/v2/actor-tasks/{forum_scraper_task_id}/runs?token={apify_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            {
              key: "keywords",
              value:
                '["email automation","workflow builder","marketing automation","sales engagement"]',
            },
            { key: "forums", value: '["reddit","quora","stackexchange"]' },
            { key: "maxResults", value: "20" },
          ],
          variables: [
            {
              name: "items[0].title",
              customName: "question_title",
              type: "string",
            },
            {
              name: "items[0].body",
              customName: "question_body",
              type: "string",
            },
            {
              name: "items[0].url",
              customName: "question_url",
              type: "string",
            },
            {
              name: "items[0].author",
              customName: "question_author",
              type: "string",
            },
            {
              name: "items[0].forum",
              customName: "forum_source",
              type: "string",
            },
            {
              name: "items[0].upvotes",
              customName: "question_upvotes",
              type: "string",
            },
            {
              name: "items[0].posted_at",
              customName: "question_posted_at",
              type: "string",
            },
            {
              name: "totalItems",
              customName: "total_questions_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Relevance filtering runCode
      n("3", "runCode", "Filter Questions by Relevance and Recency", 520, {
        runCodeDetails: {
          code: `const title = (variables.question_title || '').toLowerCase();
const body = (variables.question_body || '').toLowerCase();
const upvotes = parseInt(variables.question_upvotes) || 0;
const fullText = title + ' ' + body;
let relevanceScore = 0;
const highRelevance = ['email automation','workflow','sales engagement','drip campaign','sequence builder','outreach tool'];
const medRelevance = ['marketing tool','crm integration','lead nurture','follow up automation','email platform'];
highRelevance.forEach(kw => { if (fullText.includes(kw)) relevanceScore += 3; });
medRelevance.forEach(kw => { if (fullText.includes(kw)) relevanceScore += 1; });
if (upvotes >= 10) relevanceScore += 2;
else if (upvotes >= 5) relevanceScore += 1;
let isRelevant = relevanceScore >= 3;
let answerOpportunity = body.includes('?') || title.includes('?') || title.includes('how') || title.includes('best') || title.includes('recommend');
output.relevance_score = relevanceScore;
output.is_relevant = isRelevant ? 'yes' : 'no';
output.answer_opportunity = answerOpportunity ? 'yes' : 'no';
output.engagement_value = upvotes >= 10 ? 'high_visibility' : upvotes >= 5 ? 'moderate_visibility' : 'low_visibility';`,
          outputVariables: [
            { name: "relevance_score", type: "number" },
            { name: "is_relevant", type: "string" },
            { name: "answer_opportunity", type: "string" },
            { name: "engagement_value", type: "string" },
          ],
        },
      }),

      // 4. AI helpful answer generation
      aiNode(
        "4",
        "AI Generate Helpful Forum Answer",
        760,
        "You are a helpful community expert. Generate a genuinely useful answer to the forum question that provides real value. The answer should demonstrate expertise, reference specific techniques or best practices, and naturally mention how our product addresses the challenge — but NEVER be overtly promotional. Focus 80% on helpful content and 20% on subtle product positioning. Return JSON: {answer_text, expertise_signals, product_mention_style, answer_length, engagement_hooks, follow_up_question}",
        "Forum: {forum_source} | Question: {question_title} | Body: {question_body} | Upvotes: {question_upvotes} | Relevance: {relevance_score} | Engagement value: {engagement_value}",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          {
            field: "expertise_signals",
            as: "expertise_signals",
            type: "string",
          },
          {
            field: "product_mention_style",
            as: "product_mention_style",
            type: "string",
          },
          { field: "engagement_hooks", as: "engagement_hooks", type: "string" },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
        ],
      ),

      // 5. Commenter company identification runCode
      n("5", "runCode", "Identify Question Author Company", 1000, {
        runCodeDetails: {
          code: `const author = (variables.question_author || '').trim();
const body = (variables.question_body || '');
let companyMentions = [];
const companyPatterns = /(?:at|@|from|work(?:ing)? (?:at|for))\s+([A-Z][a-zA-Z0-9]+(?:\s+[A-Z][a-zA-Z0-9]+)?)/g;
let match;
while ((match = companyPatterns.exec(body)) !== null) {
  companyMentions.push(match[1]);
}
const authorParts = author.split(/[_\-\.]/);
let inferredCompany = companyMentions[0] || '';
output.author_username = author;
output.inferred_company = inferredCompany;
output.company_identified = inferredCompany ? 'yes' : 'no';
output.icp_check_needed = inferredCompany ? 'yes' : 'no';`,
          outputVariables: [
            { name: "author_username", type: "string" },
            { name: "inferred_company", type: "string" },
            { name: "company_identified", type: "string" },
            { name: "icp_check_needed", type: "string" },
          ],
        },
      }),

      // 6. ICP qualification condition
      n(
        "6",
        "condition",
        "Does question meet relevance and engagement threshold?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "is_relevant", operator: "equals", value: "yes" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Forum response posting approval gate (output_1)
      n("7", "approvalGate", "Review and Approve Forum Response", 1480, {
        approvalGateDetails: {
          approvers: ["content-team@company.com"],
          message:
            "Forum answer ready for review\\n\\nForum: {forum_source}\\nQuestion: {question_title}\\nURL: {question_url}\\nUpvotes: {question_upvotes} | Relevance: {relevance_score}\\nAuthor: {author_username} (Company: {inferred_company})\\n\\nProposed Answer:\\n{answer_text}\\n\\nProduct mention style: {product_mention_style}\\nEngagement hooks: {engagement_hooks}\\n\\nApprove to post this response.",
          timeout: 12,
        },
      }),

      // 8. Direct outreach + CRM lead HubSpot
      hubspotPostNode(
        "8",
        "Create Forum Lead in HubSpot CRM",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"firstname":"{author_username}","company":"{inferred_company}","hs_lead_status":"NEW","notes_last_updated":"Forum lead from {forum_source}. Question: {question_title}. URL: {question_url}. Relevance: {relevance_score}. Engagement: {engagement_value}. Company identified: {company_identified}. Answer posted with {product_mention_style} product mention. Follow-up: {follow_up_question}."}',
          },
        ],
        [{ name: "id", customName: "forum_lead_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1843. NO-RESPONSE FOLLOW-UP SEQUENCE (7 nodes)
  // ===================================================================

  "No-Response Follow-up Sequence": {
    summary:
      "Runs daily on a schedule, detects non-responses from HubSpot, verifies contact status with runCode, AI-selects follow-up strategy, AI-generates a follow-up email, calculates optimal send time with runCode, and sends the follow-up with CRM progression tracking via email and HubSpot.",
    nodes: [
      trigger("Schedule: Daily non-response check", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to identify contacts who have not responded to outbound emails within the expected response window, generate personalized follow-ups using different strategies, and maintain sequence progression in the CRM",
        setup:
          "Configure HubSpot token for engagement queries and CRM updates. Set OpenAI API key for strategy selection and follow-up generation. Set email credentials for follow-up sends.",
        inputFields: [],
      }),

      // 2. Non-response detection HubSpot
      hubspotGetNode(
        "2",
        "Detect Non-Responding Contacts in HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_send_date",
            customName: "last_email_date",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "associated_deals",
            type: "string",
          },
          { name: "total", customName: "total_non_responders", type: "number" },
        ],
      ),

      // 3. Status verification runCode
      n("3", "runCode", "Verify Contact Status and Sequence Position", 520, {
        runCodeDetails: {
          code: `const lastEmailDate = variables.last_email_date || '';
const associatedDeals = parseInt(variables.associated_deals) || 0;
const now = new Date();
let daysSinceLastEmail = 0;
if (lastEmailDate) {
  const lastDate = new Date(lastEmailDate);
  daysSinceLastEmail = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
}
let sequencePosition = 'unknown';
let followUpEligible = false;
if (daysSinceLastEmail >= 3 && daysSinceLastEmail <= 5) {
  sequencePosition = 'first_follow_up';
  followUpEligible = true;
} else if (daysSinceLastEmail >= 7 && daysSinceLastEmail <= 10) {
  sequencePosition = 'second_follow_up';
  followUpEligible = true;
} else if (daysSinceLastEmail >= 14 && daysSinceLastEmail <= 21) {
  sequencePosition = 'breakup_email';
  followUpEligible = true;
} else if (daysSinceLastEmail > 21) {
  sequencePosition = 'sequence_exhausted';
  followUpEligible = false;
}
output.days_since_last_email = daysSinceLastEmail;
output.sequence_position = sequencePosition;
output.follow_up_eligible = followUpEligible ? 'yes' : 'no';
output.has_active_deal = associatedDeals > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "days_since_last_email", type: "number" },
            { name: "sequence_position", type: "string" },
            { name: "follow_up_eligible", type: "string" },
            { name: "has_active_deal", type: "string" },
          ],
        },
      }),

      // 4. AI strategy selection
      aiNode(
        "4",
        "AI Select Follow-Up Strategy",
        760,
        "You are an expert sales follow-up strategist. Based on the sequence position, days since last contact, contact role, and deal status, select the optimal follow-up strategy. Options include: value-add (share relevant content), social proof (reference similar company success), direct question (ask a specific yes/no question), pattern interrupt (change the format entirely), and breakup (final message with low-pressure CTA). Return JSON: {strategy_name, strategy_rationale, tone, email_length, cta_type, subject_line_approach, ps_line}",
        "Contact: {contact_first} {contact_last}, {contact_title} at {contact_company} | Days since last email: {days_since_last_email} | Sequence position: {sequence_position} | Active deal: {has_active_deal} | Eligible: {follow_up_eligible}",
        [
          { field: "strategy_name", as: "strategy_name", type: "string" },
          {
            field: "strategy_rationale",
            as: "strategy_rationale",
            type: "string",
          },
          { field: "tone", as: "follow_up_tone", type: "string" },
          { field: "email_length", as: "email_length", type: "string" },
          { field: "cta_type", as: "cta_type", type: "string" },
          {
            field: "subject_line_approach",
            as: "subject_approach",
            type: "string",
          },
        ],
      ),

      // 5. AI follow-up generation
      aiNode(
        "5",
        "AI Generate Personalized Follow-Up",
        1000,
        "You are an expert cold email copywriter. Generate a follow-up email using the selected strategy. Keep it concise, personalized, and focused on a single CTA. The subject line should be short and curiosity-driven. The body should not reference the previous email being ignored. Return JSON: {follow_up_subject, follow_up_body, preview_text}",
        "Strategy: {strategy_name} | Tone: {follow_up_tone} | Length: {email_length} | CTA: {cta_type} | Subject approach: {subject_approach} | Contact: {contact_first} {contact_last}, {contact_title} at {contact_company} | Position: {sequence_position}",
        [
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "follow_up_body", as: "follow_up_body", type: "string" },
          { field: "preview_text", as: "preview_text", type: "string" },
        ],
      ),

      // 6. Send time calculation runCode
      n("6", "runCode", "Calculate Optimal Send Time", 1240, {
        runCodeDetails: {
          code: `const title = (variables.contact_title || '').toLowerCase();
const position = (variables.sequence_position || '');
let optimalHour = 9;
let optimalDay = 'Tuesday';
if (title.includes('ceo') || title.includes('founder') || title.includes('president')) {
  optimalHour = 7;
  optimalDay = 'Tuesday';
} else if (title.includes('vp') || title.includes('director')) {
  optimalHour = 8;
  optimalDay = 'Wednesday';
} else if (title.includes('manager')) {
  optimalHour = 10;
  optimalDay = 'Thursday';
}
if (position === 'breakup_email') {
  optimalHour = 16;
  optimalDay = 'Friday';
}
output.optimal_send_hour = optimalHour;
output.optimal_send_day = optimalDay;
output.send_timing_note = 'Sending at ' + optimalHour + ':00 on ' + optimalDay + ' — optimized for ' + title + ' at ' + position + ' stage';`,
          outputVariables: [
            { name: "optimal_send_hour", type: "number" },
            { name: "optimal_send_day", type: "string" },
            { name: "send_timing_note", type: "string" },
          ],
        },
      }),

      // 7. Follow-up send + CRM progression
      emailNode(
        "7",
        "Send Follow-Up Email and Update CRM",
        1480,
        "{contact_email}",
        "{follow_up_subject}",
        "{follow_up_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1844. NO-SHOW RESCHEDULE AUTOMATION (7 nodes)
  // ===================================================================

  "No-Show Reschedule Automation": {
    summary:
      "Fires when a no-show is detected via webhook, AI-extracts meeting data, verifies the grace period with runCode, AI-drafts a reschedule email, sends immediately via email, applies a 48-hour delay, then routes through a cadence management condition to either continue follow-up or close, and updates HubSpot CRM with no-show tracking.",
    nodes: [
      trigger("Webhook: Meeting no-show detected", "webhook_app", {
        source: "calendly",
        event: "invitee.no_show",
        howItStarts:
          "Fires when a meeting attendee is marked as a no-show in Calendly or scheduling tool, sending the raw no-show event with invitee details, meeting time, and host information",
        setup:
          "Configure Calendly webhook for invitee.no_show events. Set OpenAI API key for reschedule message generation. Set email credentials for immediate outreach. Configure HubSpot token for no-show tracking.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI Extract No-Show Meeting Data",
        280,
        "You are a scheduling event parser. Extract the invitee email, invitee name, invitee company, meeting type, scheduled date, scheduled time, host email, host name, meeting duration, scheduling link, and no-show count from the raw webhook. Return JSON: {invitee_email, invitee_name, invitee_company, meeting_type, scheduled_date, scheduled_time, host_email, host_name, meeting_duration, scheduling_link, no_show_count}",
        "Raw no-show detection webhook: {webhook_payload}. Extract all meeting and invitee data regardless of field naming.",
        [
          { field: "invitee_email", as: "invitee_email", type: "string" },
          { field: "invitee_name", as: "invitee_name", type: "string" },
          { field: "invitee_company", as: "invitee_company", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "scheduled_date", as: "scheduled_date", type: "string" },
          { field: "scheduled_time", as: "scheduled_time", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "scheduling_link", as: "scheduling_link", type: "string" },
          { field: "no_show_count", as: "no_show_count", type: "string" },
        ],
      ),

      // 3. Grace period verification runCode
      n("3", "runCode", "Verify Grace Period and No-Show History", 520, {
        runCodeDetails: {
          code: `const scheduledTime = variables.scheduled_time || '';
const noShowCount = parseInt(variables.no_show_count) || 1;
const now = new Date();
let minutesPastScheduled = 15;
if (scheduledTime) {
  try {
    const scheduled = new Date(scheduledTime);
    minutesPastScheduled = Math.floor((now.getTime() - scheduled.getTime()) / (1000 * 60));
  } catch(e) {}
}
let graceConfirmed = minutesPastScheduled >= 10;
let rescheduleApproach = '';
if (noShowCount >= 3) {
  rescheduleApproach = 'final_attempt';
} else if (noShowCount === 2) {
  rescheduleApproach = 'gentle_persistence';
} else {
  rescheduleApproach = 'understanding_flexibility';
}
output.grace_confirmed = graceConfirmed ? 'yes' : 'no';
output.minutes_past = minutesPastScheduled;
output.reschedule_approach = rescheduleApproach;
output.total_no_shows = noShowCount;
output.should_continue = noShowCount < 3 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "grace_confirmed", type: "string" },
            { name: "minutes_past", type: "number" },
            { name: "reschedule_approach", type: "string" },
            { name: "total_no_shows", type: "number" },
            { name: "should_continue", type: "string" },
          ],
        },
      }),

      // 4. AI reschedule draft
      aiNode(
        "4",
        "AI Draft Reschedule Email",
        760,
        "You are an expert at handling meeting no-shows gracefully. Draft a reschedule email that is understanding, not guilt-tripping, and makes it easy to rebook. Adjust tone based on the number of no-shows. For first no-show: assume something came up. For repeat: be more direct but still professional. Include the scheduling link prominently. Return JSON: {reschedule_subject, reschedule_body}",
        "Invitee: {invitee_name} at {invitee_company} | Meeting: {meeting_type} | Scheduled: {scheduled_date} | Host: {host_name} | No-show count: {total_no_shows} | Approach: {reschedule_approach} | Scheduling link: {scheduling_link}",
        [
          {
            field: "reschedule_subject",
            as: "reschedule_subject",
            type: "string",
          },
          { field: "reschedule_body", as: "reschedule_body", type: "string" },
        ],
      ),

      // 5. Immediate send email
      emailNode(
        "5",
        "Send Reschedule Email Immediately",
        1000,
        "{invitee_email}",
        "{reschedule_subject}",
        "{reschedule_body}",
      ),

      // 6. Cadence management condition
      n("6", "condition", "Should follow-up cadence continue?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "should_continue", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. CRM no-show tracking HubSpot (output_1 = continue, output_2 = stop)
      hubspotPostNode(
        "7",
        "Update No-Show Tracking in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts/search?query={invitee_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"No-show #{total_no_shows} for {meeting_type} on {scheduled_date}. Reschedule approach: {reschedule_approach}. Reschedule email sent. Continue follow-up: {should_continue}. Scheduling link: {scheduling_link}. Host: {host_name}."}',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "noshow_contact_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1845. OBJECTION EXTRACTION RESPONSE (7 nodes)
  // ===================================================================

  "Objection Extraction Response": {
    summary:
      "Fires when a prospect reply is detected via webhook, AI-classifies the objection type, AI-analyzes the context behind the objection, AI-generates a tailored response, routes through an approval queue, sends the approved response via email, and logs the objection in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Prospect reply detected", "webhook_app", {
        source: "gmail",
        event: "message.received",
        howItStarts:
          "Fires when a prospect replies to an outbound email, sending the raw email reply event with sender, subject, body, thread ID, and original campaign context",
        setup:
          "Configure Gmail webhook or email parsing service for reply detection. Set OpenAI API key for objection classification and response generation. Set email credentials for response delivery. Configure HubSpot token for objection logging.",
        inputFields: [],
      }),

      // 2. AI objection classification
      aiNode(
        "2",
        "AI Classify Objection Type",
        280,
        "You are an expert sales objection analyst. Parse the raw reply webhook to extract the sender email, sender name, sender company, reply subject, reply body, original thread subject, and original campaign name. Then classify the objection into one of these categories: pricing, timing, authority, need, competitor, feature_gap, contract_terms, no_interest, already_using, or positive_response. Return JSON: {sender_email, sender_name, sender_company, reply_subject, reply_body, original_subject, objection_type, objection_severity, contains_question, sentiment, is_objection}",
        "Raw reply webhook: {webhook_payload}. Extract all reply data and classify the objection regardless of how the reply is structured.",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_company", as: "sender_company", type: "string" },
          { field: "reply_body", as: "reply_body", type: "string" },
          { field: "original_subject", as: "original_subject", type: "string" },
          { field: "objection_type", as: "objection_type", type: "string" },
          {
            field: "objection_severity",
            as: "objection_severity",
            type: "string",
          },
          { field: "sentiment", as: "reply_sentiment", type: "string" },
          { field: "is_objection", as: "is_objection", type: "string" },
        ],
      ),

      // 3. AI context analysis
      aiNode(
        "3",
        "AI Analyze Objection Context",
        520,
        "You are a sales psychology expert. Analyze the objection in depth to understand the underlying concern. For pricing objections, determine if it is budget, ROI, or comparison-driven. For timing objections, determine if it is genuine or a polite brush-off. For authority objections, identify who the real decision maker might be. Return JSON: {underlying_concern, buyer_psychology, decision_stage, urgency_indicator, negotiation_leverage, recommended_counter_approach, confidence_level, escalation_needed}",
        "Objection type: {objection_type} | Severity: {objection_severity} | Reply: {reply_body} | Sentiment: {reply_sentiment} | Company: {sender_company} | Original subject: {original_subject}",
        [
          {
            field: "underlying_concern",
            as: "underlying_concern",
            type: "string",
          },
          { field: "buyer_psychology", as: "buyer_psychology", type: "string" },
          {
            field: "recommended_counter_approach",
            as: "counter_approach",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          {
            field: "negotiation_leverage",
            as: "negotiation_leverage",
            type: "string",
          },
        ],
      ),

      // 4. AI tailored response generation
      aiNode(
        "4",
        "AI Generate Tailored Objection Response",
        760,
        "You are an expert sales communicator. Generate a response that directly addresses the underlying concern, uses the recommended counter approach, and moves the conversation forward. Do not be defensive or dismissive. Acknowledge the objection, provide specific evidence or data points, and end with a clear next step. Return JSON: {response_subject, response_body, evidence_used, next_step_proposed}",
        "Objection: {objection_type} | Concern: {underlying_concern} | Psychology: {buyer_psychology} | Counter approach: {counter_approach} | Leverage: {negotiation_leverage} | Sender: {sender_name} at {sender_company} | Confidence: {confidence_level}",
        [
          { field: "response_subject", as: "response_subject", type: "string" },
          { field: "response_body", as: "response_body", type: "string" },
          { field: "evidence_used", as: "evidence_used", type: "string" },
          {
            field: "next_step_proposed",
            as: "next_step_proposed",
            type: "string",
          },
        ],
      ),

      // 5. Approval queue
      n("5", "approvalGate", "Approve Objection Response Before Send", 1000, {
        approvalGateDetails: {
          approvers: ["sales-manager@company.com"],
          message:
            "Objection response ready for review\\n\\nProspect: {sender_name} ({sender_email}) at {sender_company}\\nObjection Type: {objection_type} | Severity: {objection_severity}\\nSentiment: {reply_sentiment}\\n\\nOriginal Reply: {reply_body}\\n\\nUnderlying Concern: {underlying_concern}\\nCounter Approach: {counter_approach}\\nConfidence: {confidence_level}\\n\\nProposed Response:\\nSubject: {response_subject}\\nBody: {response_body}\\n\\nEvidence: {evidence_used}\\nNext Step: {next_step_proposed}\\n\\nApprove to send.",
          timeout: 8,
        },
      }),

      // 6. Approved response send email
      emailNode(
        "6",
        "Send Approved Objection Response",
        1240,
        "{sender_email}",
        "{response_subject}",
        "{response_body}",
      ),

      // 7. CRM objection log HubSpot
      hubspotPostNode(
        "7",
        "Log Objection to HubSpot CRM",
        1480,
        "crm/v3/objects/contacts/search?query={sender_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Objection handled — Type: {objection_type}. Severity: {objection_severity}. Concern: {underlying_concern}. Counter: {counter_approach}. Response sent with {evidence_used}. Next step: {next_step_proposed}. Confidence: {confidence_level}. Escalation: {escalation_needed}."}',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "objection_contact_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1846. OBJECTION LIBRARY MATCH RESPONSE (7 nodes)
  // ===================================================================

  "Objection Library Match Response": {
    summary:
      "Fires when an objection is captured via webhook, normalizes the text with runCode, performs a semantic search against the objection library via API, selects the best response with runCode, AI-personalizes the response, delivers the suggested response via Slack, and updates the objection analytics in Sheets.",
    nodes: [
      trigger("Webhook: Objection captured from conversation", "webhook_app", {
        source: "gong",
        event: "call.objection_detected",
        howItStarts:
          "Fires when an objection is detected during a sales call or email exchange, sending the raw objection event with the objection text, context, caller details, and conversation metadata",
        setup:
          "Configure Gong or call intelligence webhook for objection detection. Set vector search API credentials for semantic matching. Set OpenAI API key for response personalization. Configure Slack webhook for response delivery. Set Google Sheets for analytics logging.",
        inputFields: [],
      }),

      // 2. AI extract + text normalization runCode
      aiNode(
        "2",
        "AI Extract Objection Event Data",
        280,
        "You are a call intelligence event parser. Extract the objection text, objection context (what was being discussed), caller name, caller email, prospect name, prospect email, prospect company, prospect title, deal ID, deal stage, and call ID from the raw webhook. Return JSON: {objection_text, objection_context, caller_name, caller_email, prospect_name, prospect_email, prospect_company, prospect_title, deal_id, deal_stage, call_id}",
        "Raw objection detection webhook: {webhook_payload}. Extract all objection and conversation data regardless of field naming.",
        [
          { field: "objection_text", as: "objection_text", type: "string" },
          {
            field: "objection_context",
            as: "objection_context",
            type: "string",
          },
          { field: "caller_name", as: "caller_name", type: "string" },
          { field: "caller_email", as: "caller_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
        ],
      ),

      // 3. Text normalization runCode
      n("3", "runCode", "Normalize Objection Text for Matching", 520, {
        runCodeDetails: {
          code: `const raw = (variables.objection_text || '').trim();
const normalized = raw.toLowerCase()
  .replace(/[^a-z0-9\\s]/g, '')
  .replace(/\\s+/g, ' ')
  .trim();
let category = 'unknown';
if (normalized.includes('price') || normalized.includes('cost') || normalized.includes('expensive') || normalized.includes('budget')) category = 'pricing';
else if (normalized.includes('time') || normalized.includes('busy') || normalized.includes('not now') || normalized.includes('next quarter')) category = 'timing';
else if (normalized.includes('competitor') || normalized.includes('already using') || normalized.includes('current solution')) category = 'competitor';
else if (normalized.includes('feature') || normalized.includes('missing') || normalized.includes('need') || normalized.includes('require')) category = 'feature_gap';
else if (normalized.includes('decision') || normalized.includes('boss') || normalized.includes('team') || normalized.includes('approval')) category = 'authority';
else if (normalized.includes('not interested') || normalized.includes('no thanks') || normalized.includes('remove')) category = 'no_interest';
output.normalized_text = normalized;
output.objection_category = category;
output.word_count = normalized.split(' ').length;
output.search_query = normalized.substring(0, 200);`,
          outputVariables: [
            { name: "normalized_text", type: "string" },
            { name: "objection_category", type: "string" },
            { name: "word_count", type: "number" },
            { name: "search_query", type: "string" },
          ],
        },
      }),

      // 4. Semantic search API
      n("4", "hitApi", "Search Objection Library via Semantic Match", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "namespace", value: "objection-library" },
            { key: "topK", value: "3" },
            { key: "includeMetadata", value: "true" },
            { key: "vector", value: "{search_query}" },
          ],
          variables: [
            {
              name: "matches[0].metadata.response",
              customName: "best_response_text",
              type: "string",
            },
            {
              name: "matches[0].metadata.category",
              customName: "matched_category",
              type: "string",
            },
            {
              name: "matches[0].metadata.success_rate",
              customName: "response_success_rate",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "match_confidence",
              type: "string",
            },
            {
              name: "matches[1].metadata.response",
              customName: "alt_response_text",
              type: "string",
            },
            {
              name: "matches[1].score",
              customName: "alt_match_confidence",
              type: "string",
            },
          ],
        },
      }),

      // 5. Best response selection runCode
      n("5", "runCode", "Select Best Response from Library Matches", 1000, {
        runCodeDetails: {
          code: `const bestResponse = (variables.best_response_text || '').trim();
const altResponse = (variables.alt_response_text || '').trim();
const bestConfidence = parseFloat(variables.match_confidence) || 0;
const altConfidence = parseFloat(variables.alt_match_confidence) || 0;
const successRate = (variables.response_success_rate || '0');
const matchedCategory = (variables.matched_category || '');
const objectionCategory = (variables.objection_category || '');
let selectedResponse = bestResponse;
let selectionReason = '';
let confidenceLevel = '';
if (bestConfidence >= 0.85) {
  selectedResponse = bestResponse;
  selectionReason = 'High-confidence match (' + (bestConfidence * 100).toFixed(0) + '%) — using primary library response';
  confidenceLevel = 'high';
} else if (bestConfidence >= 0.65) {
  selectedResponse = bestResponse;
  selectionReason = 'Moderate match (' + (bestConfidence * 100).toFixed(0) + '%) — using primary but recommend personalization';
  confidenceLevel = 'medium';
} else {
  selectedResponse = altResponse || bestResponse;
  selectionReason = 'Low match (' + (bestConfidence * 100).toFixed(0) + '%) — heavy personalization recommended';
  confidenceLevel = 'low';
}
output.selected_response = selectedResponse;
output.selection_reason = selectionReason;
output.response_confidence = confidenceLevel;
output.category_match = matchedCategory === objectionCategory ? 'exact' : 'related';
output.historical_success_rate = successRate;`,
          outputVariables: [
            { name: "selected_response", type: "string" },
            { name: "selection_reason", type: "string" },
            { name: "response_confidence", type: "string" },
            { name: "category_match", type: "string" },
            { name: "historical_success_rate", type: "string" },
          ],
        },
      }),

      // 6. AI response personalization + Slack delivery
      slackNode(
        "6",
        "Deliver Personalized Response via Slack",
        1240,
        "Objection Response Ready — *{objection_category}*\\n\\n*Prospect:* {prospect_name}, {prospect_title} at {prospect_company}\\n*Deal:* {deal_id} ({deal_stage})\\n*Rep:* {caller_name}\\n\\n*Objection:* {objection_text}\\n*Context:* {objection_context}\\n\\n*Library Match:* {response_confidence} confidence | Category: {category_match}\\n*Success Rate:* {historical_success_rate}\\n*Reason:* {selection_reason}\\n\\n*Suggested Response:*\\n{selected_response}\\n\\nPersonalize for {prospect_name}'s specific situation before using.",
      ),

      // 7. Objection analytics update Sheets
      sheetsNode(
        "7",
        "Update Objection Analytics in Sheets",
        1480,
        "ObjectionAnalytics",
        `[["${new Date().toISOString().split("T")[0]}", "{objection_category}", "{normalized_text}", "{prospect_company}", "{prospect_title}", "{deal_stage}", "{response_confidence}", "{category_match}", "{historical_success_rate}", "{caller_name}", "{selection_reason}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1847. OUT-OF-OFFICE RESCHEDULER (7 nodes)
  // ===================================================================

  "Out-of-Office Rescheduler": {
    summary:
      "Fires when an out-of-office auto-reply is detected via webhook, AI-extracts the return date, identifies any delegate with runCode, pauses the sequence with runCode, schedules a return-date delay, AI-drafts a follow-up for the return, and updates HubSpot CRM with sequence resume and email send.",
    nodes: [
      trigger("Webhook: Out-of-office reply detected", "webhook_app", {
        source: "gmail",
        event: "message.autoReply",
        howItStarts:
          "Fires when an out-of-office auto-reply is detected from a prospect, sending the raw auto-reply event with the OOO message body, sender, original thread context, and reply metadata",
        setup:
          "Configure Gmail webhook or email parsing service for auto-reply detection. Set OpenAI API key for return date extraction and follow-up generation. Configure HubSpot token for sequence management and CRM updates. Set email credentials for timed follow-up sends.",
        inputFields: [],
      }),

      // 2. AI return date extraction
      aiNode(
        "2",
        "AI Extract OOO Return Date and Details",
        280,
        "You are an out-of-office message parser. Extract the sender email, sender name, sender company, OOO message body, return date, delegate name, delegate email, OOO reason (vacation, conference, leave, etc.), original thread subject, and original campaign context from the raw auto-reply webhook. Parse natural language dates like 'back on Monday' or 'returning January 15th' into ISO format. Return JSON: {sender_email, sender_name, sender_company, ooo_body, return_date, delegate_name, delegate_email, ooo_reason, original_subject, has_delegate}",
        "Raw out-of-office webhook: {webhook_payload}. Extract all OOO details including the return date regardless of date format or phrasing.",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_company", as: "sender_company", type: "string" },
          { field: "ooo_body", as: "ooo_body", type: "string" },
          { field: "return_date", as: "return_date", type: "string" },
          { field: "delegate_name", as: "delegate_name", type: "string" },
          { field: "delegate_email", as: "delegate_email", type: "string" },
          { field: "ooo_reason", as: "ooo_reason", type: "string" },
          { field: "original_subject", as: "original_subject", type: "string" },
          { field: "has_delegate", as: "has_delegate", type: "string" },
        ],
      ),

      // 3. Delegate identification runCode
      n("3", "runCode", "Process OOO Details and Delegate Info", 520, {
        runCodeDetails: {
          code: `const returnDate = (variables.return_date || '').trim();
const hasDelegate = (variables.has_delegate || '').toLowerCase() === 'true' || (variables.has_delegate || '').toLowerCase() === 'yes';
const delegateName = (variables.delegate_name || '').trim();
const delegateEmail = (variables.delegate_email || '').trim();
const oooReason = (variables.ooo_reason || '').toLowerCase();
let daysUntilReturn = 7;
if (returnDate) {
  try {
    const retDate = new Date(returnDate);
    const now = new Date();
    daysUntilReturn = Math.max(1, Math.ceil((retDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  } catch(e) {
    daysUntilReturn = 7;
  }
}
let followUpDelay = daysUntilReturn + 1;
let delegateAction = 'none';
if (hasDelegate && delegateEmail) {
  delegateAction = 'contact_delegate';
} else if (hasDelegate && !delegateEmail) {
  delegateAction = 'delegate_mentioned_no_email';
}
let priority = 'normal';
if (oooReason.includes('vacation') || oooReason.includes('holiday')) priority = 'normal';
else if (oooReason.includes('conference') || oooReason.includes('travel')) priority = 'high';
else if (oooReason.includes('leave') || oooReason.includes('medical')) priority = 'low';
output.days_until_return = daysUntilReturn;
output.follow_up_delay_days = followUpDelay;
output.delegate_action = delegateAction;
output.reschedule_priority = priority;
output.return_date_confirmed = returnDate ? 'yes' : 'estimated';`,
          outputVariables: [
            { name: "days_until_return", type: "number" },
            { name: "follow_up_delay_days", type: "number" },
            { name: "delegate_action", type: "string" },
            { name: "reschedule_priority", type: "string" },
            { name: "return_date_confirmed", type: "string" },
          ],
        },
      }),

      // 4. Sequence pause runCode
      n("4", "runCode", "Pause Active Sequences for OOO Contact", 760, {
        runCodeDetails: {
          code: `const senderEmail = (variables.sender_email || '');
const followUpDelay = parseInt(variables.follow_up_delay_days) || 7;
const delegateAction = (variables.delegate_action || '');
let pauseActions = [];
pauseActions.push('Paused all active sequences for ' + senderEmail);
pauseActions.push('Scheduled resume in ' + followUpDelay + ' days');
if (delegateAction === 'contact_delegate') {
  pauseActions.push('Delegate available: ' + variables.delegate_name + ' (' + variables.delegate_email + ') — consider parallel outreach');
}
output.pause_summary = pauseActions.join('; ');
output.sequences_paused = 1;
output.resume_date = new Date(Date.now() + followUpDelay * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
output.status = 'paused_awaiting_return';`,
          outputVariables: [
            { name: "pause_summary", type: "string" },
            { name: "sequences_paused", type: "number" },
            { name: "resume_date", type: "string" },
            { name: "status", type: "string" },
          ],
        },
      }),

      // 5. Return date scheduling delay
      n("5", "delayAction", "Wait Until Prospect Returns from OOO", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Waiting {follow_up_delay_days} days until {sender_name} returns on {return_date}. Resume date: {resume_date}. All sequences paused.",
        },
      }),

      // 6. AI follow-up draft
      aiNode(
        "6",
        "AI Draft Post-OOO Follow-Up",
        1240,
        "You are an expert at warm re-engagement. Draft a follow-up email that references the prospect being away (without being creepy about it), re-establishes context from the original conversation, and provides a fresh reason to engage. Keep it light and helpful. Return JSON: {follow_up_subject, follow_up_body, reengagement_hook}",
        "Prospect: {sender_name} at {sender_company} | Was away: {ooo_reason} | Return: {return_date} | Original subject: {original_subject} | Days away: {days_until_return} | Priority: {reschedule_priority}",
        [
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "follow_up_body", as: "follow_up_body", type: "string" },
          {
            field: "reengagement_hook",
            as: "reengagement_hook",
            type: "string",
          },
        ],
      ),

      // 7. CRM update + resume email HubSpot
      hubspotPostNode(
        "7",
        "Update CRM and Resume Outreach",
        1480,
        "crm/v3/objects/contacts/search?query={sender_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"OOO handled — Reason: {ooo_reason}. Return: {return_date}. Paused {sequences_paused} sequences. Delegate: {delegate_action}. Follow-up sent on {resume_date} with subject: {follow_up_subject}. Re-engagement: {reengagement_hook}. Priority: {reschedule_priority}."}',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "ooo_contact_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
